import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    makeStyles,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import CloseIcon from '@material-ui/icons/Close';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import DoneOutlinedIcon from '@material-ui/icons/DoneOutlined';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import EventOutlinedIcon from '@material-ui/icons/EventOutlined';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import NoteAddOutlinedIcon from '@material-ui/icons/NoteAddOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RemoveCircleOutlineOutlinedIcon from '@material-ui/icons/RemoveCircleOutlineOutlined';
import ReplayOutlinedIcon from '@material-ui/icons/ReplayOutlined';
import ReceiptOutlinedIcon from '@material-ui/icons/ReceiptOutlined';
import SearchIcon from '@material-ui/icons/Search';
import StopOutlinedIcon from '@material-ui/icons/StopOutlined';
import TuneIcon from '@material-ui/icons/Tune';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import { FlashOn, Opacity, Whatshot, CheckCircle, RadioButtonUnchecked } from '@material-ui/icons';
import { Alert } from '@mui/material';
import BillInvoiceEditor from './BillInvoiceEditor';
import Container from '../Elements/Container';
import {
    abortBillProcessing,
    createManualBill,
    deleteBill,
    discardBillSuggestion,
    downloadBillFile,
    exportBillsCsv,
    getBillDetail,
    getBillProcessingHistory,
    getBillProcessingProgress,
    getBills,
    getOrphanBillsDetailed,
    getPendingRevisionBills,
    linkBill,
    markBillsAsReviewed,
    patchVerificacionAutomatica,
    retryBillProcessing,
    uploadBillFile,
} from '../../services/billing-v2';
import { getNodes } from '../../services/hierarchy';
import { getLocationServices, createLocationService, updateLocationService, deleteLocationService } from '../../services/localizacion-servicio';
import { getUnassignedMeters } from '../../services/meters';
import TopNav from '../common/TopNav';
import UsageIndicator from '../common/UsageIndicator';


const useStyles = makeStyles(theme => ({
    '@keyframes pmProgressSlide': {
        '0%': { backgroundPosition: '100% 0' },
        '100%': { backgroundPosition: '-100% 0' },
    },
    root: {
        padding: theme.spacing(3),
        background: `linear-gradient(180deg, ${theme.palette.surface.panel} 0%, ${theme.palette.background.default} 100%)`,
    },
    topActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    header: {
        marginBottom: theme.spacing(3),
        padding: theme.spacing(1, 0),
        background: 'transparent',
        boxShadow: 'none',
    },
    titleRow: {
        gap: theme.spacing(2),
    },
    titleIcon: {
        width: 54,
        height: 54,
        borderRadius: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.surface.accentSoft,
        color: theme.palette.primary.main,
    },
    title: {
        fontWeight: 700,
        color: theme.palette.grey.main,
    },
    subtitle: {
        color: theme.palette.text.secondary,
    },
    summaryCard: {
        height: '100%',
        padding: theme.spacing(2),
        borderRadius: 5,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.border.soft}`,
    },
    summaryCardGreen: {
        borderLeft: `3px solid ${theme.palette.status.success}`,
    },
    summaryCardRed: {
        borderLeft: `3px solid ${theme.palette.error.main}`,
    },
    summaryLabel: {
        fontSize: 13,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
    },
    summaryValue: {
        fontSize: 26,
        fontWeight: 700,
        color: theme.palette.primary.main,
        lineHeight: 1.1,
    },
    summaryCaption: {
        fontSize: 12,
        color: theme.palette.text.disabled,
        marginTop: theme.spacing(0.5),
    },
    filtersPanel: {
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2.5),
        border: `1px solid ${theme.palette.border.main}`,
        borderRadius: 5,
        backgroundColor: theme.palette.common.white,
        boxShadow: theme.palette.shadow.search,
    },
    filterField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 5,
            backgroundColor: theme.palette.surface.popover,
        },
        '& .MuiOutlinedInput-input': {
            paddingTop: theme.spacing(1.1),
            paddingBottom: theme.spacing(1.1),
        },
        '& .MuiInputLabel-outlined': {
            fontSize: 13,
        },
    },
    filterActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
    },
    orphanBanner: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(2.5),
        padding: theme.spacing(1.5, 2),
        borderRadius: 5,
        border: `1px solid ${theme.palette.warning.main}`,
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.dark,
        cursor: 'pointer',
        transition: 'box-shadow 0.15s ease',
        '&:hover': {
            boxShadow: '0 2px 8px rgba(180, 83, 9, 0.12)',
        },
    },
    orphanBannerText: {
        flex: 1,
        fontSize: 13,
        fontWeight: 500,
        color: theme.palette.warning.dark,
        lineHeight: 1.4,
    },
    orphanBannerAction: {
        fontSize: 12,
        fontWeight: 700,
        color: theme.palette.warning.dark,
        border: `1px solid ${theme.palette.warning.main}`,
        borderRadius: 4,
        padding: theme.spacing(0.3, 1),
        whiteSpace: 'nowrap',
        backgroundColor: 'rgba(255,255,255,0.5)',
    },
    orphanCard: {
        padding: theme.spacing(2, 2.5),
        borderRadius: 5,
        border: `1px solid ${theme.palette.border.soft}`,
        backgroundColor: theme.palette.background.default,
        boxShadow: theme.palette.shadow.card,
        marginBottom: theme.spacing(1.5),
        '&:last-child': {
            marginBottom: 0,
        },
    },
    orphanCardHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(0.75),
    },
    orphanCardTitle: {
        fontWeight: 700,
        fontSize: 14,
        color: theme.palette.text.primary,
    },
    orphanCardMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        fontSize: 12,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.5),
    },
    suggestionBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(0.875, 1.5),
        borderRadius: 5,
        backgroundColor: theme.palette.surface.accentSoft,
        border: `1px solid ${theme.palette.surface.accentBorder}`,
        margin: theme.spacing(1.25, 0),
    },
    suggestionBadgeText: {
        flex: 1,
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.primary.main,
    },
    suggestionConfidence: {
        fontSize: 11,
        fontWeight: 500,
        color: theme.palette.text.disabled,
        backgroundColor: theme.palette.surface.neutral,
        border: `1px solid ${theme.palette.border.subtle}`,
        borderRadius: 4,
        padding: theme.spacing(0.2, 0.75),
        whiteSpace: 'nowrap',
    },
    orphanActions: {
        display: 'flex',
        gap: theme.spacing(0.75),
        flexWrap: 'wrap',
        marginTop: theme.spacing(1.5),
        paddingTop: theme.spacing(1.5),
        borderTop: `1px solid ${theme.palette.border.subtle}`,
    },
    orphanAcceptBtn: {
        fontSize: 12,
        fontWeight: 700,
        textTransform: 'none',
        borderRadius: 5,
        padding: theme.spacing(0.5, 1.5),
        color: theme.palette.common.white,
        background: `linear-gradient(180deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
        boxShadow: theme.palette.shadow.accent,
        '&:hover': {
            background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        },
        '&.Mui-disabled': {
            opacity: 0.6,
            boxShadow: 'none',
        },
    },
    orphanSecondaryBtn: {
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: 5,
        padding: theme.spacing(0.5, 1.5),
        color: theme.palette.text.secondary,
        border: `1px solid ${theme.palette.border.button}`,
        backgroundColor: theme.palette.surface.alt,
        '&:hover': {
            backgroundColor: theme.palette.surface.neutral,
        },
    },
    orphanDiscardBtn: {
        fontSize: 12,
        fontWeight: 500,
        textTransform: 'none',
        borderRadius: 5,
        padding: theme.spacing(0.5, 1.25),
        color: theme.palette.text.disabled,
        '&:hover': {
            color: theme.palette.text.secondary,
            backgroundColor: theme.palette.surface.alt,
        },
    },
    jerarquiaListItem: {
        padding: theme.spacing(1, 1.5),
        borderRadius: 5,
        cursor: 'pointer',
        transition: 'background-color 0.12s ease',
        '&:hover': {
            backgroundColor: theme.palette.surface.accentArea,
        },
    },
    jerarquiaListItemSelected: {
        backgroundColor: theme.palette.surface.selected,
        '&:hover': {
            backgroundColor: theme.palette.surface.selectedChild,
        },
    },
    listContainer: {
        borderRadius: 5,
        border: `1px solid ${theme.palette.border.soft}`,
        boxShadow: theme.palette.shadow.card,
        overflow: 'hidden',
        backgroundColor: theme.palette.common.white,
    },
    centeredState: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 180,
    },
    primaryActionButton: {
        borderRadius: 5,
        padding: theme.spacing(0.6, 1.75),
        textTransform: 'none',
        fontWeight: 700,
        fontSize: 13,
        color: theme.palette.common.white,
        background: `linear-gradient(180deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
        boxShadow: theme.palette.shadow.accent,
        '&:hover': {
            background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        },
    },
    secondaryTopActionButton: {
        position: 'relative',
        borderRadius: 5,
        padding: theme.spacing(0.6, 1.75),
        textTransform: 'none',
        fontWeight: 600,
        fontSize: 13,
        border: '1px solid rgba(255,255,255,0.35)',
        color: 'rgba(255,255,255,0.9)',
        backgroundColor: 'rgba(255,255,255,0.12)',
        '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.2)',
        },
    },
    processingButtonContent: {
        display: 'flex',
        alignItems: 'center',
    },
    processingBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: theme.spacing(0.75),
        minWidth: 18,
        height: 18,
        padding: theme.spacing(0, 0.5),
        borderRadius: 9,
        fontSize: 11,
        fontWeight: 700,
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.common.white,
    },
    exportButton: {
        borderRadius: 5,
        padding: 0,
        textTransform: 'none',
        fontWeight: 600,
        fontSize: 13,
        border: `1px solid ${theme.palette.border.button}`,
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.surface.alt,
        boxShadow: theme.palette.shadow.card,
        overflow: 'hidden',
        '&:hover': {
            backgroundColor: theme.palette.surface.neutral,
        },
    },
    exportLabel: {
        padding: theme.spacing(0.6, 1.75),
    },
    exportArrow: {
        borderLeft: `1px solid ${theme.palette.border.button}`,
        borderRadius: 0,
        padding: theme.spacing(0.6),
        color: theme.palette.text.secondary,
    },
    tableHead: {
        backgroundColor: theme.palette.surface.panel,
    },
    headCell: {
        color: theme.palette.text.section,
        fontWeight: 700,
        borderBottom: `2px solid ${theme.palette.border.main}`,
        whiteSpace: 'nowrap',
        fontSize: 12,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    bodyRow: {
        '&:nth-child(even)': {
            backgroundColor: theme.palette.surface.soft,
        },
    },
    code: {
        color: theme.palette.primary.main,
        fontWeight: 700,
        cursor: 'pointer',
        display: 'inline-flex',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    client: {
        fontWeight: 700,
        color: theme.palette.grey.main,
    },
    bodyCell: {
        borderBottom: `1px solid ${theme.palette.border.subtle}`,
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
    },
    period: {
        fontWeight: 600,
        color: theme.palette.text.secondary,
    },
    totalValue: {
        fontWeight: 700,
        color: theme.palette.grey.main,
    },
    locationCell: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    statusChip: {
        fontWeight: 500,
        width: 108,
        height: 28,
        borderRadius: 4,
        border: `1px solid ${theme.palette.border.button}`,
        '& .MuiChip-label': {
            paddingLeft: theme.spacing(1.25),
            paddingRight: theme.spacing(1.25),
        },
        '& .MuiChip-icon, & .MuiChip-deleteIcon, &::before, &::after': {
            display: 'none',
        },
    },
    actionsCell: {
        textAlign: 'right',
    },
    actionsRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    serviceTypeTag: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 22,
        height: 22,
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 700,
        flexShrink: 0,
    },
    iconActionButton: {
        width: 36,
        height: 36,
        borderRadius: 5,
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.surface.accentSoft,
        '&:hover': {
            backgroundColor: theme.palette.surface.selectedChild,
        },
        '&.Mui-disabled': {
            color: theme.palette.text.disabled,
            backgroundColor: theme.palette.surface.neutral,
        },
    },
    button: {
        borderRadius: 5,
        padding: theme.spacing(0.75, 2),
        textTransform: 'none',
        fontWeight: 700,
    },
    uploadDialog: {
        '& .MuiDialog-paper': {
            borderRadius: theme.borderRadiusBoxes.borderRadius,
            padding: theme.spacing(1),
            width: 'calc(100vw - 48px)',
            maxWidth: 980,
            minHeight: 'calc(50vh)',
            margin: theme.spacing(2),
            background: `linear-gradient(180deg, ${theme.palette.surface.soft} 0%, ${theme.palette.background.default} 100%)`,
            display: 'flex',
            flexDirection: 'column',
        },
    },
    uploadDialogCompact: {
        '& .MuiDialog-paper': {
            height: 610,
            minHeight: 'unset',
        },
    },
    uploadFilesGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: theme.spacing(2),
        flex: 1,
        minHeight: 0,
        '@media (max-width: 760px)': {
            gridTemplateColumns: '1fr',
        },
    },
    uploadFilesRight: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        gap: theme.spacing(2),
        overflowY: 'auto',
        paddingRight: theme.spacing(0.5),
        '&::-webkit-scrollbar': {
            width: 8,
        },
        '&::-webkit-scrollbar-track': {
            background: theme.palette.status.scrollbarTrackLight,
            borderRadius: theme.borderRadiusBoxes.borderRadius,
        },
        '&::-webkit-scrollbar-thumb': {
            background: theme.palette.status.scrollbarThumb,
            borderRadius: theme.borderRadiusBoxes.borderRadius,
        },
    },
    uploadDialogTitle: {
        padding: theme.spacing(2.5, 2.5, 1.5),
    },
    uploadDialogHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
    },
    uploadDialogEyebrow: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
        padding: theme.spacing(0.75, 1.25),
        borderRadius: 5,
        backgroundColor: theme.palette.surface.accentSoft,
        color: theme.palette.primary.main,
        fontSize: 12,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
    },
    uploadDialogTitleText: {
        fontWeight: 700,
        color: theme.palette.grey.main,
    },
    uploadDialogSubtitle: {
        color: theme.palette.text.secondary,
    },
    uploadDialogCloseButton: {
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.surface.neutral,
        borderRadius: 5,
        '&:hover': {
            backgroundColor: theme.palette.surface.selectedChild,
        },
    },
    uploadDialogContent: {
        padding: theme.spacing(1, 2.5, 2.5),
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
    },
    uploadDialogContentOptions: {
        paddingBottom: theme.spacing(2),
    },
    uploadOptionsGrid: {
        width: '100%',
        maxWidth: 900,
        margin: `${theme.spacing(1)}px auto 0`,
        alignItems: 'stretch',
        flex: 1,
        overflowY: 'auto',
        minHeight: 0,
    },
    uploadOptionCard: {
        height: '100%',
        minHeight: 280,
        padding: theme.spacing(2.5),
        borderRadius: 12,
        border: `1px solid ${theme.palette.border.soft}`,
        backgroundColor: theme.palette.common.white,
        boxShadow: theme.palette.shadow.card,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.palette.shadow.sticky,
        },
        '@media (max-width: 760px)': {
            minHeight: 'auto',
        },
    },
    uploadOptionCardSelected: {
        borderColor: theme.palette.primary.main,
        boxShadow: theme.palette.shadow.accent,
        background: `linear-gradient(180deg, ${theme.palette.surface.accentArea} 0%, ${theme.palette.background.default} 100%)`,
    },
    uploadOptionCardPremium: {
        borderColor: 'rgba(245, 158, 11, 0.45)',
        background: 'linear-gradient(160deg, #fffdf5 0%, #ffffff 65%)',
        boxShadow: '0 4px 16px rgba(245, 158, 11, 0.12)',
        cursor: 'pointer',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 28px rgba(245, 158, 11, 0.2)',
            borderColor: 'rgba(245, 158, 11, 0.65)',
        },
    },
    premiumBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: theme.spacing(0.45, 1),
        borderRadius: 99,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        backgroundColor: '#fef3c7',
        color: '#92400e',
        marginBottom: theme.spacing(1.5),
        border: '1px solid rgba(245, 158, 11, 0.3)',
        width: 'fit-content',
    },
    premiumOptionIcon: {
        backgroundColor: '#fef3c7',
        color: '#d97706',
    },
    premiumFeatureList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        marginTop: theme.spacing(1.5),
        marginBottom: theme.spacing(2),
    },
    premiumFeatureItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 13,
        color: theme.palette.text.secondary,
        '& .MuiSvgIcon-root': {
            fontSize: 14,
            color: '#f59e0b',
            flexShrink: 0,
        },
    },
    upgradeButton: {
        textTransform: 'none',
        fontWeight: 700,
        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: '#ffffff',
        borderRadius: theme.borderRadiusBoxes.borderRadius,
        '&:hover': {
            background: `linear-gradient(90deg, #1a6e9e 0%, ${theme.palette.primary.main} 100%)`,
        },
    },
    uploadOptionIcon: {
        width: 56,
        height: 56,
        borderRadius: 12,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: theme.spacing(2),
        backgroundColor: theme.palette.surface.accentSoft,
        color: theme.palette.primary.main,
    },
    uploadOptionTitle: {
        fontWeight: 700,
        color: theme.palette.grey.main,
        marginBottom: theme.spacing(1),
    },
    uploadOptionDescription: {
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(2),
        flex: 1,
    },
    uploadOptionTag: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: theme.spacing(0.75, 1.25),
        borderRadius: 5,
        fontSize: 12,
        fontWeight: 700,
        backgroundColor: theme.palette.surface.neutral,
        color: theme.palette.text.secondary,
    },
    uploadDialogFooter: {
        marginTop: theme.spacing(3),
        padding: theme.spacing(2),
        borderRadius: 12,
        border: `1px solid ${theme.palette.border.soft}`,
        backgroundColor: theme.palette.surface.soft,
    },
    uploadDialogFooterText: {
        color: theme.palette.text.secondary,
    },
    uploadDropzone: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(4, 3),
        border: `2px dashed ${theme.palette.border.input}`,
        borderRadius: 12,
        backgroundColor: theme.palette.surface.popover,
        textAlign: 'center',
        transition: 'border-color 0.2s ease, background-color 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        minHeight: 320,
    },
    uploadDropzoneActive: {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.surface.accentArea,
    },
    uploadDropzoneIcon: {
        width: 68,
        height: 68,
        borderRadius: 14,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.surface.accentSoft,
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(2),
    },
    uploadDropzoneTitle: {
        fontWeight: 700,
        color: theme.palette.grey.main,
        marginBottom: theme.spacing(1),
    },
    uploadDropzoneText: {
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(2),
    },
    uploadDropzoneHint: {
        color: theme.palette.text.disabled,
        fontSize: 12,
        marginTop: theme.spacing(1.5),
    },
    hiddenInput: {
        display: 'none',
    },
    filesSummary: {
        marginTop: theme.spacing(2.5),
        padding: theme.spacing(1.5),
        borderRadius: 12,
        border: `1px solid ${theme.palette.border.soft}`,
        backgroundColor: theme.palette.background.default,
    },
    filesSummaryHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1.5),
    },
    filesSummaryTitle: {
        fontWeight: 700,
        color: theme.palette.grey.main,
    },
    filesSummaryCount: {
        color: theme.palette.text.secondary,
        fontSize: 13,
    },
    filesSummaryList: {
        maxHeight: 280,
        overflowY: 'auto',
        paddingRight: theme.spacing(0.5),
        '&::-webkit-scrollbar': {
            width: 8,
        },
        '&::-webkit-scrollbar-track': {
            background: theme.palette.status.scrollbarTrackLight,
            borderRadius: theme.borderRadiusBoxes.borderRadius,
        },
        '&::-webkit-scrollbar-thumb': {
            background: theme.palette.status.scrollbarThumb,
            borderRadius: theme.borderRadiusBoxes.borderRadius,
        },
    },
    fileItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        padding: theme.spacing(0.75, 0),
        borderTop: `1px solid ${theme.palette.border.subtle}`,
        '&:first-child': {
            borderTop: 'none',
            paddingTop: 0,
        },
        '&:last-child': {
            paddingBottom: 0,
        },
    },
    fileItemInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0,
        flex: 1,
    },
    fileItemIcon: {
        width: 28,
        height: 28,
        flexShrink: 0,
        borderRadius: 5,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.surface.neutral,
        color: theme.palette.text.secondary,
    },
    fileItemName: {
        fontWeight: 600,
        fontSize: 13,
        color: theme.palette.grey.main,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    fileItemMeta: {
        fontSize: 11,
        color: theme.palette.text.disabled,
    },
    fileItemActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        flexShrink: 0,
    },
    compactTag: {
        padding: theme.spacing(0.35, 0.85),
        fontSize: 11,
        lineHeight: 1.2,
    },
    uploadInlineMessage: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(1.5, 2),
        borderRadius: 5,
        border: `1px solid ${theme.palette.border.soft}`,
        backgroundColor: theme.palette.surface.soft,
        color: theme.palette.text.secondary,
    },
    uploadInlineMessageError: {
        borderColor: theme.palette.error.light,
        backgroundColor: theme.palette.error.light,
        color: theme.palette.error.dark,
    },
    uploadInlineMessageWarning: {
        borderColor: '#f59e0b',
        backgroundColor: '#fffbeb',
        color: '#92400e',
    },
    uploadProgressBar: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(1.75, 2),
        borderRadius: 5,
        border: `1px solid ${theme.palette.border.soft}`,
        backgroundColor: theme.palette.common.white,
        boxShadow: theme.palette.shadow.card,
    },
    uploadProgressHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(1),
    },
    uploadProgressLabel: {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.grey.main,
    },
    uploadProgressPct: {
        fontSize: 13,
        fontWeight: 700,
        color: theme.palette.primary.main,
    },
    uploadProgressTrack: {
        height: 8,
        borderRadius: 99,
        backgroundColor: theme.palette.surface.neutral,
        overflow: 'hidden',
    },
    uploadProgressFill: {
        height: '100%',
        borderRadius: 99,
        transition: 'width 0.3s ease',
    },
    uploadJobsPanel: {
        marginTop: theme.spacing(2.5),
        padding: theme.spacing(2),
        borderRadius: 12,
        border: `1px solid ${theme.palette.border.soft}`,
        backgroundColor: theme.palette.background.default,
    },
    uploadJobsHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5),
        '@media (max-width: 760px)': {
            alignItems: 'flex-start',
            flexDirection: 'column',
        },
    },
    uploadJobsTitle: {
        fontWeight: 700,
        color: theme.palette.grey.main,
    },
    uploadJobsCaption: {
        color: theme.palette.text.secondary,
        fontSize: 13,
    },
    uploadJobsList: {
        display: 'grid',
        gap: theme.spacing(1.5),
    },
    uploadJobCard: {
        padding: theme.spacing(1.75),
        borderRadius: 5,
        border: `1px solid ${theme.palette.border.soft}`,
        backgroundColor: theme.palette.surface.popover,
    },
    compactUploadJobCard: {
        padding: theme.spacing(0.75, 1.25),
        borderRadius: 5,
        border: `1px solid ${theme.palette.border.soft}`,
        backgroundColor: theme.palette.surface.popover,
    },
    compactJobRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0,
    },
    compactJobFilename: {
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontWeight: 600,
        fontSize: 13,
        color: theme.palette.grey.main,
    },
    scoreChip: {
        fontSize: 11,
        fontWeight: 700,
        padding: theme.spacing(0.25, 0.75),
        borderRadius: 4,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        cursor: 'default',
    },
    processingJobRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(0.6, 1),
        borderRadius: 5,
        border: `1px solid ${theme.palette.border.soft}`,
        backgroundColor: theme.palette.surface.popover,
        minWidth: 0,
    },
    processingJobRowName: {
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.grey.main,
    },
    processingJobRowBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(0.3, 0.9),
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 700,
        flexShrink: 0,
        backgroundColor: theme.palette.surface.accentSoft,
        color: theme.palette.primary.main,
    },
    uploadJobHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        '@media (max-width: 760px)': {
            flexDirection: 'column',
        },
    },
    uploadJobInfo: {
        minWidth: 0,
        flex: 1,
    },
    uploadJobHeaderMain: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        minWidth: 0,
        flex: 1,
    },
    uploadJobAside: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: theme.spacing(1),
        flexShrink: 0,
        '@media (max-width: 760px)': {
            width: '100%',
            alignItems: 'flex-start',
        },
    },
    uploadJobName: {
        fontWeight: 700,
        color: theme.palette.grey.main,
        wordBreak: 'break-word',
    },
    uploadJobMeta: {
        fontSize: 12,
        color: theme.palette.text.disabled,
        marginTop: theme.spacing(0.5),
    },
    uploadJobStatus: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 110,
        padding: theme.spacing(0.75, 1.25),
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 700,
    },
    uploadJobProgress: {
        marginTop: theme.spacing(1.25),
        color: theme.palette.text.secondary,
    },
    uploadJobActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1.5),
        flexWrap: 'wrap',
    },
    uploadActionButton: {
        borderRadius: 5,
        padding: theme.spacing(0.75, 1.5),
        textTransform: 'none',
        fontWeight: 700,
    },
    uploadIconActions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: theme.spacing(0.5),
    },
    uploadIconButton: {
        padding: theme.spacing(0.5),
    },
    uploadSelectionBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5),
        '@media (max-width: 760px)': {
            flexDirection: 'column',
            alignItems: 'flex-start',
        },
    },
    uploadSelectionInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    uploadSelectionLabel: {
        fontSize: 13,
        color: theme.palette.text.secondary,
    },
    emptyJobsState: {
        color: theme.palette.text.disabled,
    },
    uploadDialogActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1.5),
        marginTop: theme.spacing(2),
        flexShrink: 0,
        '@media (max-width: 760px)': {
            flexDirection: 'column-reverse',
            '& > *': {
                width: '100%',
            },
        },
    },
    uploadDialogActionsOptions: {
        maxWidth: 900,
        width: '100%',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    secondaryActionButton: {
        borderRadius: 5,
        padding: theme.spacing(1, 2.25),
        textTransform: 'none',
        fontWeight: 700,
        border: `1px solid ${theme.palette.border.button}`,
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.background.default,
    },
    detailDialog: {
        '& .MuiDialog-paper': {
            borderRadius: theme.borderRadiusBoxes.borderRadius,
            width: 'calc(100vw - 48px)',
            maxWidth: 1480,
            margin: theme.spacing(2),
            maxHeight: 'calc(100vh - 32px)',
            overflow: 'auto',
        },
    },
    detailDialogContent: {
        padding: 0,
        overflow: 'unset',
        '& .MuiDialogContent-root': {
            padding: 0,
            overflow: 'unset',
        },
    },
    detailBody: {
        display: 'grid',
        gap: theme.spacing(2),
        paddingTop: theme.spacing(1),
    },
    detailSummaryGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: theme.spacing(1.5),
        '@media (max-width: 760px)': {
            gridTemplateColumns: '1fr',
        },
    },
    detailCard: {
        padding: theme.spacing(2),
        borderRadius: 5,
        border: `1px solid ${theme.palette.border.soft}`,
        backgroundColor: theme.palette.surface.popover,
    },
    detailLabel: {
        color: theme.palette.text.disabled,
        fontSize: 12,
        marginBottom: theme.spacing(0.5),
    },
    detailValue: {
        color: theme.palette.grey.main,
        fontWeight: 700,
        wordBreak: 'break-word',
    },
    detailSection: {
        padding: theme.spacing(2),
        borderRadius: 5,
        border: `1px solid ${theme.palette.border.soft}`,
        backgroundColor: theme.palette.background.default,
    },
    detailSectionTitle: {
        fontWeight: 700,
        color: theme.palette.grey.main,
        marginBottom: theme.spacing(1.5),
    },
    detailFieldRow: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        padding: theme.spacing(1.25, 0),
        borderTop: `1px solid ${theme.palette.border.subtle}`,
        '&:first-child': {
            borderTop: 'none',
            paddingTop: 0,
        },
        '@media (max-width: 760px)': {
            flexDirection: 'column',
        },
    },
    detailFieldName: {
        fontWeight: 600,
        color: theme.palette.grey.main,
    },
    detailFieldMeta: {
        color: theme.palette.text.secondary,
        fontSize: 13,
    },
    markdownPreview: {
        maxHeight: 220,
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        color: theme.palette.text.secondary,
        fontFamily: 'monospace',
        fontSize: 12,
        margin: 0,
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(3, 2),
    },
    emptyStateIcon: {
        fontSize: '48px !important',
        color: theme.palette.border.muted,
    },
    emptyStateText: {
        color: theme.palette.text.disabled,
    },
    servicesDialog: {
        '& .MuiDialog-paper': {
            borderRadius: theme.borderRadiusBoxes.borderRadius,
            width: 'calc(100vw - 48px)',
            maxWidth: 860,
            margin: theme.spacing(2),
        },
    },
    servicesGrid: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
        marginTop: 16,
    },
    serviceCard: {
        border: `1px solid ${theme.palette.border.soft}`,
        borderRadius: 8,
        overflow: 'hidden',
        flex: '1 1 0',
        minWidth: 220,
    },
    serviceCardHeader: {
        padding: '8px 14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        color: 'white',
        fontWeight: 700,
        fontSize: 14,
    },
    serviceCardHeaderLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    serviceCardBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 12,
        fontWeight: 400,
    },
    serviceCardBody: {
        padding: '12px 14px 8px',
    },
    serviceLabel: {
        fontSize: 12,
        fontWeight: 600,
        color: '#555',
        marginBottom: 4,
    },
    serviceCardFooter: {
        display: 'flex',
        gap: 8,
        padding: '4px 14px 14px',
    },
    servicesLocationSelector: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        marginTop: 8,
    },
}));

const currencyFormatter = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const normalizeDateToDisplay = value => {
    if (!value || value === '-') {
        return '-';
    }

    if (typeof value === 'string' && value.includes('/')) {
        return value;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

const getStatusConfig = (theme, status) => {
    if (status === 'OK') {
        return {
            label: 'Procesada',
            backgroundColor: theme.palette.status.successSoft,
            color: theme.palette.status.success,
        };
    }

    if (status === 'ERROR') {
        return {
            label: 'Error',
            backgroundColor: theme.palette.error.light,
            color: theme.palette.error.dark,
        };
    }

    if (status === 'ABORTADO') {
        return {
            label: 'Abortada',
            backgroundColor: theme.palette.warning.light,
            color: theme.palette.warning.dark,
        };
    }

    return {
        label: 'Procesando',
        backgroundColor: theme.palette.surface.accentSoft,
        color: theme.palette.primary.main,
    };
};

const normalizeBill = bill => ({
    id: bill?.id,
    uid: bill?.uid || bill?.record_id || bill?.id,
    tipo: bill?.tipo || bill?.tipo_servicio || '',
    code:
        bill?.numero_factura ||
        bill?.code ||
        bill?.name ||
        bill?.nombre ||
        `FAC-${String(bill?.id || '').padStart(6, '0')}`,
    client:
        bill?.proveedor_nombre ||
        bill?.proveedor ||
        bill?.client ||
        bill?.client_name ||
        bill?.location_name ||
        bill?.location?.name ||
        'Sin proveedor',
    period:
        bill?.period ||
        (bill?.periodo_desde || bill?.periodo_hasta
            ? `${normalizeDateToDisplay(bill?.periodo_desde || '-')} - ${normalizeDateToDisplay(bill?.periodo_hasta || '-')}`
            : bill?.period_name || bill?.periodo || 'Sin periodo'),
    issuedAt: normalizeDateToDisplay(bill?.issuedAt || bill?.issued_at || bill?.date_from || bill?.fecha_emision || '-'),
    dueAt: normalizeDateToDisplay(bill?.dueAt || bill?.due_at || bill?.date_to || bill?.fecha_vencimiento || '-'),
    location:
        bill?.jerarquia_nombre ||
        bill?.location_name ||
        bill?.location?.name ||
        bill?.location ||
        bill?.hierarchy_name ||
        (bill?.es_huerfana ? 'Sin vincular' : '-'),
    consumption: bill?.consumption || bill?.consumo || bill?.energy_use_total || bill?.energy_total || '-',
    total: Number(bill?.total || bill?.total_amount || bill?.factura?.total || 0),
    status: bill?.status || bill?.estado || 'PROCESSING',
    score: bill?.score ?? null,
    score_original: bill?.score_original ?? null,
    campos_corregidos_count: bill?.campos_corregidos_count ?? 0,
    esHuerfana: Boolean(bill?.es_huerfana),
    jerarquiaSugeridaId: bill?.jerarquia_sugerida_id ?? null,
    jerarquiaSugeridaNombre: bill?.jerarquia_sugerida_nombre ?? null,
    scoreSugerencia: bill?.score_sugerencia ?? null,
    moneda: bill?.moneda || 'ARS',
    archivoUrl: bill?.archivo_url || bill?.file_url || bill?.resultado?.archivo_url || '',
});

const formatFileSize = size => {
    if (!Number.isFinite(size) || size <= 0) {
        return '0 KB';
    }

    if (size < 1024 * 1024) {
        return `${Math.max(1, Math.round(size / 1024))} KB`;
    }

    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const getUploadStatusConfig = (theme, status) => {
    if (status === 'OK') {
        return {
            label: 'Procesada',
            backgroundColor: theme.palette.status.successSoft,
            color: theme.palette.status.success,
        };
    }

    if (status === 'ERROR') {
        return {
            label: 'Error',
            backgroundColor: theme.palette.error.light,
            color: theme.palette.error.dark,
        };
    }

    if (status === 'ABORTADO') {
        return {
            label: 'Abortada',
            backgroundColor: theme.palette.warning.light,
            color: theme.palette.warning.dark,
        };
    }

    return {
        label: 'Procesando',
        backgroundColor: theme.palette.surface.accentSoft,
        color: theme.palette.primary.main,
    };
};

const getServiceTypeColor = tipo => {
    if (tipo === 'gas') return '#1d4ed8';
    if (tipo === 'agua') return '#0e7490';
    return '#92400e';
};

const getServiceTypeBg = tipo => {
    if (tipo === 'gas') return '#dbeafe';
    if (tipo === 'agua') return '#cffafe';
    return '#fef3c7';
};

const createEmptyManualBill = () => ({
    tipo: '',
    proveedor: '',
    jerarquia_nombre: '',
    numero_factura: '',
    clase: 'Factura A',
    archivo_url: '',
    detalle_facturacion: [],
    impuestos: [],
    ajustes: [],
    factura: {},
    factura_papel: {},
});

const buildFallbackBillDetail = bill => ({
    id: bill?.id,
    tipo: bill?.tipo || '',
    proveedor: bill?.client || '',
    jerarquia_nombre: bill?.location || '',
    numero_factura: bill?.code || '',
    archivo_url: bill?.archivoUrl || '',
    estado: bill?.status || 'PROCESSING',
    total: bill?.total || 0,
    consumo: bill?.consumption || '',
    detalle_facturacion: [],
    impuestos: [],
    ajustes: [],
    factura: {},
    factura_papel: {},
});

const applyEditorFormToBillDetail = (currentDetail = {}, formData = {}) => ({
    ...currentDetail,
    proveedor: formData?.proveedor || '',
    jerarquia_nombre: formData?.sitio || '',
    jerarquia_id: formData?.idSitio || currentDetail?.jerarquia_id || '',
    tipo_servicio: formData?.tipoServicio || currentDetail?.tipo_servicio || 'electricidad',
    periodo_desde: formData?.periodoDesde ?? currentDetail?.periodo_desde ?? '',
    periodo_hasta: formData?.periodoHasta ?? currentDetail?.periodo_hasta ?? '',
    fecha_emision: formData?.fechaEmision ?? currentDetail?.fecha_emision ?? '',
    fecha_vencimiento: formData?.fechaVencimiento ?? currentDetail?.fecha_vencimiento ?? '',
    numero_factura: formData?.numeroFactura || '',
    clase: formData?.clase || '',
    archivo_url: formData?.archivoUrl || currentDetail?.archivo_url || '',
    archivo_nombre: formData?.archivoNombre || currentDetail?.archivo_nombre || '',
    detalle_facturacion: Array.isArray(formData?.cargos) ? formData.cargos : [],
    impuestos: Array.isArray(formData?.impuestos) ? formData.impuestos : [],
    ajustes: Array.isArray(formData?.ajustes) ? formData.ajustes : [],
});

const buildBillRowFromEditorForm = (formData = {}, currentBill = null) => {
    const chargeTotal = Array.isArray(formData?.cargos)
        ? formData.cargos.reduce((acc, item) => acc + Number(item?.subtotal || 0), 0)
        : Number(currentBill?.total || 0);
    const taxesTotal = Array.isArray(formData?.impuestos)
        ? formData.impuestos.reduce((acc, item) => acc + Number(item?.subtotal || 0), 0)
        : 0;
    const adjustmentsTotal = Array.isArray(formData?.ajustes)
        ? formData.ajustes.reduce((acc, item) => acc + Number(item?.subtotal || 0), 0)
        : 0;
    const total = chargeTotal + taxesTotal + adjustmentsTotal;
    const energyItem = Array.isArray(formData?.cargos)
        ? formData.cargos.find(item => String(item?.concepto || '').toLowerCase().includes('energia'))
        : null;

    return {
        ...(currentBill || {}),
        id: currentBill?.id || `manual-${Date.now()}`,
        uid: currentBill?.uid || currentBill?.id || `manual-${Date.now()}`,
        tipo: formData?.tipoServicio || currentBill?.tipo || 'electricidad',
        code: formData?.numeroFactura || currentBill?.code || 'Sin numero',
        client: formData?.proveedor || currentBill?.client || 'Sin cliente',
        period:
            formData?.periodoDesde || formData?.periodoHasta
                ? `${formData?.periodoDesde || '-'} - ${formData?.periodoHasta || '-'}`
                : currentBill?.period || 'Sin periodo',
        issuedAt: formData?.fechaEmision || currentBill?.issuedAt || '-',
        dueAt: formData?.fechaVencimiento || currentBill?.dueAt || '-',
        location: formData?.sitio || currentBill?.location || '-',
        consumption: energyItem?.cantidad ? `${energyItem.cantidad} ${energyItem.unidad || ''}`.trim() : currentBill?.consumption || '-',
        total,
        status: currentBill?.status || 'OK',
        archivoUrl: formData?.archivoUrl || currentBill?.archivoUrl || '',
    };
};

const buildBillRowFromProcessedJob = processedJob =>
    buildBillRowFromEditorForm(
        {
            proveedor: processedJob?.billData?.proveedor || '',
            sitio: processedJob?.billData?.jerarquia_nombre || '',
            tipoServicio: processedJob?.billData?.tipo_servicio || processedJob?.billTipo || 'electricidad',
            periodoDesde: processedJob?.billData?.periodo_desde || '',
            periodoHasta: processedJob?.billData?.periodo_hasta || '',
            fechaEmision: processedJob?.billData?.fecha_emision || '',
            fechaVencimiento: processedJob?.billData?.fecha_vencimiento || '',
            numeroFactura: processedJob?.billData?.numero_factura || '',
            clase: processedJob?.billData?.clase || 'Factura A',
            archivoUrl: processedJob?.billData?.archivo_url || processedJob?.archivoUrl || '',
            archivoNombre: processedJob?.billData?.archivo_nombre || processedJob?.filename || '',
            cargos: processedJob?.billData?.detalle_facturacion || [],
            impuestos: processedJob?.billData?.impuestos || [],
            ajustes: processedJob?.billData?.ajustes || [],
        },
        {
            id: processedJob?.billId || processedJob?.id,
            uid: processedJob?.billId || processedJob?.id,
            status: 'OK',
        }
    );

const buildManualBillPayload = formData => {
    const tipo = formData.tipoServicio || 'electricidad';

    const findCargo = name =>
        formData.cargos?.find(c => String(c.concepto || '').toLowerCase() === name.toLowerCase());
    const findCargoIncludes = name =>
        formData.cargos?.find(c => String(c.concepto || '').toLowerCase().includes(name.toLowerCase()));

    const cargoFijo = findCargo('Cargo fijo');
    const cargoVariable = findCargo('Cargo variable');
    const potenciaContratada = findCargoIncludes('Potencia contratada');
    const potenciaDemandada = findCargoIncludes('Potencia demandada');
    const potenciaExcedida = findCargoIncludes('Potencia excedida');

    const sumList = list => (list || []).reduce((acc, c) => acc + (parseFloat(c.subtotal) || 0), 0);
    const round2 = n => Math.round(n * 100) / 100;
    const chargesSum = round2(sumList(formData.cargos));
    const total = round2(chargesSum + sumList(formData.impuestos) + sumList(formData.ajustes));

    const payload = { tipo, moneda: 'ARS' };

    if (formData.proveedorId) payload.proveedor = parseInt(formData.proveedorId, 10);
    if (formData.proveedor) payload.proveedor_nombre = formData.proveedor;
    if (formData.idSitio) payload.jerarquia_id = parseInt(formData.idSitio, 10);
    if (formData.numeroFactura) payload.numero_factura = formData.numeroFactura;
    if (formData.periodoDesde) payload.periodo_desde = formData.periodoDesde;
    if (formData.periodoHasta) payload.periodo_hasta = formData.periodoHasta;
    if (formData.idCliente) payload.numero_cuenta = formData.idCliente;
    else if (formData.numeroMedidor) payload.numero_medidor = formData.numeroMedidor;
    if (formData.fechaEmision) payload.fecha_emision = formData.fechaEmision;
    if (formData.fechaVencimiento) payload.fecha_vencimiento = formData.fechaVencimiento;
    if (formData.razonSocial) payload.cliente_razon_social = formData.razonSocial;
    if (formData.cuit) payload.cliente_cuit = formData.cuit;
    if (formData.domicilio) payload.cliente_domicilio = formData.domicilio;
    if (formData.lecturaAnterior) payload.lectura_anterior = formData.lecturaAnterior;
    if (formData.lecturaActual) payload.lectura_actual = formData.lecturaActual;
    if (formData.unidadLectura) payload.unidad_lectura = formData.unidadLectura;
    if (cargoFijo?.subtotal) payload.cargo_fijo = cargoFijo.subtotal;
    if (cargoVariable?.subtotal) payload.cargo_variable = cargoVariable.subtotal;
    if (chargesSum > 0) payload.subtotal = chargesSum.toFixed(2);
    if (total > 0) payload.total = total.toFixed(2);

    if (tipo === 'electricidad') {
        if (formData.consumo) payload.consumo_kwh = formData.consumo;
        if (potenciaContratada?.cantidad) payload.potencia_contratada_kw = potenciaContratada.cantidad;
        if (potenciaContratada?.subtotal) payload.potencia_contratada_costo = potenciaContratada.subtotal;
        if (potenciaDemandada?.cantidad) payload.potencia_demandada_kw = potenciaDemandada.cantidad;
        if (potenciaDemandada?.subtotal) payload.potencia_demandada_costo = potenciaDemandada.subtotal;
        if (potenciaExcedida?.cantidad) payload.potencia_excedida_kw = potenciaExcedida.cantidad;
        if (potenciaExcedida?.subtotal) payload.potencia_excedida_costo = potenciaExcedida.subtotal;
    } else if (tipo === 'agua' || tipo === 'gas') {
        if (formData.consumo) payload.consumo_m3 = formData.consumo;
    }

    return payload;
};

const serviceTypeOptions = [
    { value: '', label: 'Todos los servicios' },
    { value: 'electricidad', label: 'Electricidad' },
    { value: 'gas', label: 'Gas' },
    { value: 'agua', label: 'Agua' },
];

const billStatusOptions = [
    { value: '', label: 'Todos los estados' },
    { value: 'OK', label: 'Procesada' },
    { value: 'ERROR', label: 'Error' },
    { value: 'PROCESSING', label: 'Procesando' },
    { value: 'ABORTADO', label: 'Abortada' },
];

const initialFilters = {
    invoiceNumber: '',
    provider: '',
    serviceType: '',
    status: '',
    dateFrom: '',
    dateTo: '',
};

const normalizeProcessingRecord = record => ({
    id: record?.id,
    filename:
        record?.filename ||
        record?.resultado?.filename ||
        record?.nombre_archivo ||
        record?.file_name ||
        'Archivo sin nombre',
    estado: record?.estado || record?.status || 'PROCESSING',
    score: record?.score ?? record?.resultado?.score ?? null,
    errorMessage: record?.error_message || record?.detail || null,
    startedAt: record?.started_at || record?.fecha_recepcion || null,
    finishedAt: record?.finished_at || null,
    durationSeconds: record?.duracion_segundos ?? null,
    progressStep: record?.progreso?.step || null,
    progressMessage: record?.progreso?.message || null,
    batchId: record?.batch_id || record?.resultado?.batch_id || null,
    archivoUrl: record?.archivo_url || record?.resultado?.archivo_url || null,
    celeryTaskId: record?.celery_task_id || null,
    billId: record?.factura_id || record?.resultado?.factura_id || record?.resultado?.factura?.id || record?.resultado?.id || null,
    billTipo: record?.factura_tipo || record?.tipo_servicio || record?.tipo || record?.resultado?.factura_tipo || record?.resultado?.tipo_servicio || record?.resultado?.tipo || 'electricidad',
    billData: record?.resultado?.factura || record?.resultado?.bill || record?.resultado || null,
    humanRevision: record?.resultado?.factura?.human_revision ?? record?.human_revision ?? null,
});

const getFilenameFromDisposition = disposition => {
    if (!disposition) {
        return null;
    }

    const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) {
        return decodeURIComponent(utfMatch[1]);
    }

    const asciiMatch = disposition.match(/filename="?([^";]+)"?/i);
    return asciiMatch?.[1] || null;
};

export default function BillsList() {
    const classes = useStyles();
    const theme = useTheme();
    const [billsQuota, setBillsQuota] = useState(null);
    const pollingIntervalsRef = useRef({});
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [rowActionsAnchorEl, setRowActionsAnchorEl] = useState(null);
    const [selectedRowBill, setSelectedRowBill] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [rejectAllConfirmOpen, setRejectAllConfirmOpen] = useState(false);
    const [billToDelete, setBillToDelete] = useState(null);
    const [deletingBill, setDeletingBill] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [uploadOption, setUploadOption] = useState('files');
    const [uploadStep, setUploadStep] = useState('options');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isDragActive, setIsDragActive] = useState(false);
    const [uploadJobs, setUploadJobs] = useState([]);
    const [loadingUploadHistory, setLoadingUploadHistory] = useState(false);
    const [submittingFiles, setSubmittingFiles] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({
        phase: 'idle',
        completedUploads: 0,
        totalUploads: 0,
        completedJobs: 0,
        totalJobs: 0,
    });
    const prevUploadJobStatesRef = useRef({});
    const [uploadFeedback, setUploadFeedback] = useState({ type: '', message: '' });
    const [orphanSummary, setOrphanSummary] = useState({ count: 0, ids: [] });
    const [orphanItems, setOrphanItems] = useState([]);
    const [orphanDialogOpen, setOrphanDialogOpen] = useState(false);
    const [linkingBill, setLinkingBill] = useState(null);
    const [jerarquiaSelectorOpen, setJerarquiaSelectorOpen] = useState(false);
    const [jerarquiaSelectorBill, setJerarquiaSelectorBill] = useState(null);
    const [jerarquiaNodes, setJerarquiaNodes] = useState([]);
    const [jerarquiaNodesLoading, setJerarquiaNodesLoading] = useState(false);
    const [jerarquiaSearch, setJerarquiaSearch] = useState('');
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [selectedBillDetail, setSelectedBillDetail] = useState(null);
    const [editingBillRow, setEditingBillRow] = useState(null);
    const [detailMode, setDetailMode] = useState('view');
    const [detailReturnToUploadModal, setDetailReturnToUploadModal] = useState(false);
    const [selectedProcessedJobIds, setSelectedProcessedJobIds] = useState([]);
    const [bills, setBills] = useState([]);
    const [totalBillsCount, setTotalBillsCount] = useState(0);
    const [totalBillsAmount, setTotalBillsAmount] = useState(0);
    const [pendingRevisionBills, setPendingRevisionBills] = useState([]);
    const [loadingBills, setLoadingBills] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filters, setFilters] = useState(initialFilters);

    const emptyService = { id: null, id_cliente_utility: '', id_medidor_utility: '', medidor: null };
    const [servicesDialogOpen, setServicesDialogOpen] = useState(false);
    const [servicesNode, setServicesNode] = useState(null);
    const [servicesNodeSearch, setServicesNodeSearch] = useState('');
    const [servicesNodes, setServicesNodes] = useState([]);
    const [servicesNodesLoading, setServicesNodesLoading] = useState(false);
    const [servicesStep, setServicesStep] = useState('select');
    const [electricityService, setElectricityService] = useState({ ...emptyService });
    const [waterService, setWaterService] = useState({ ...emptyService });
    const [gasService, setGasService] = useState({ ...emptyService });
    const [servicesLoading, setServicesLoading] = useState(false);
    const [unassignedMeters, setUnassignedMeters] = useState([]);
    const [servicesNotification, setServicesNotification] = useState({ open: false, message: '', severity: 'success' });

    const mergeUploadJobs = jobs => {
        if (!Array.isArray(jobs) || jobs.length === 0) {
            return;
        }

        setUploadJobs(currentJobs => {
            const jobsMap = new Map(currentJobs.map(job => [job.id, job]));

            jobs.forEach(job => {
                if (!job?.id) {
                    return;
                }

                jobsMap.set(job.id, {
                    ...jobsMap.get(job.id),
                    ...job,
                });
            });

            return Array.from(jobsMap.values()).sort((left, right) => {
                if (left.estado === 'PROCESSING' && right.estado !== 'PROCESSING') {
                    return -1;
                }

                if (left.estado !== 'PROCESSING' && right.estado === 'PROCESSING') {
                    return 1;
                }

                return Number(right.id || 0) - Number(left.id || 0);
            });
        });
    };

    const stopPollingJob = recordId => {
        if (!pollingIntervalsRef.current[recordId]) {
            return;
        }

        clearInterval(pollingIntervalsRef.current[recordId]);
        delete pollingIntervalsRef.current[recordId];
    };

    const fetchBillsList = async () => {
        setLoadingBills(true);
        try {
            const response = await getBills({
                tipo_servicio: filters.serviceType || '',
                estado: filters.status || '',
                proveedor: filters.provider || '',
                fecha_emision_desde: filters.dateFrom || '',
                fecha_emision_hasta: filters.dateTo || '',
                numero_factura: filters.invoiceNumber || '',
                page: page + 1,
                page_size: rowsPerPage,
            });
            const responseData = response?.data;
            const items = Array.isArray(responseData)
                ? responseData
                : responseData?.results || responseData?.data || responseData?.items || [];
            const total =
                responseData?.count ||
                responseData?.total ||
                responseData?.total_count ||
                (Array.isArray(items) ? items.length : 0);

            if (Array.isArray(items)) {
                const normalizedBills = items.map(normalizeBill);
                setBills(normalizedBills);
                setTotalBillsCount(Number(total) || normalizedBills.length);
                const totalesPaginas = responseData?.totales_pagina;
                setTotalBillsAmount(Number(totalesPaginas?.[0]?.total) || 0);
                setBillsQuota(responseData?.cuota_facturas_ia ?? null);
            }
            else {
                setBills([]);
                setTotalBillsCount(0);
                setTotalBillsAmount(0);
                setBillsQuota(null);
            }
        }
        catch (error) {
            setBills([]);
            setTotalBillsCount(0);
            setBillsQuota(null);
        }
        finally {
            setLoadingBills(false);
        }
    };

    const fetchPendingRevisionBills = async () => {
        try {
            const response = await getPendingRevisionBills({ page_size: 100 });
            const responseData = response?.data;
            const items = Array.isArray(responseData)
                ? responseData
                : responseData?.results || responseData?.data || responseData?.items || [];
            setPendingRevisionBills(Array.isArray(items) ? items.map(normalizeBill) : []);
        }
        catch {
            setPendingRevisionBills([]);
        }
    };

    const fetchOrphanSummary = async () => {
        try {
            const response = await getOrphanBillsDetailed();
            const data = response?.data;
            const total = data?.total || data?.count || 0;
            const ids = data?.ids || [];
            const porTipo = data?.por_tipo || {};
            const items = Object.values(porTipo).flatMap(group =>
                Array.isArray(group?.items) ? group.items : []
            );
            setOrphanSummary({ count: total, ids });
            setOrphanItems(items);
        }
        catch (error) {
            setOrphanSummary({ count: 0, ids: [] });
            setOrphanItems([]);
        }
    };

    const handleOpenOrphanDialog = () => setOrphanDialogOpen(true);

    const handleCloseOrphanDialog = () => setOrphanDialogOpen(false);

    const handleAcceptSuggestion = async bill => {
        if (!bill?.jerarquia_sugerida_id) return;
        setLinkingBill({ id: bill.id, tipo: bill.tipo });
        try {
            await linkBill(bill.tipo, bill.id, bill.jerarquia_sugerida_id);
            await fetchOrphanSummary();
            await fetchBillsList();
        }
        catch {
            // keep dialog open so user can retry
        }
        finally {
            setLinkingBill(null);
        }
    };

    const handleDiscardSuggestion = async bill => {
        setLinkingBill({ id: bill.id, tipo: bill.tipo });
        try {
            await discardBillSuggestion(bill.tipo, bill.id);
            setOrphanItems(prev =>
                prev.map(item =>
                    item.id === bill.id && item.tipo === bill.tipo
                        ? { ...item, jerarquia_sugerida_id: null, jerarquia_sugerida_nombre: null, score_sugerencia: null }
                        : item
                )
            );
        }
        catch {
            // ignore
        }
        finally {
            setLinkingBill(null);
        }
    };

    const handleOpenJerarquiaSelector = async bill => {
        setJerarquiaSelectorBill(bill);
        setJerarquiaSearch('');
        setJerarquiaSelectorOpen(true);
        if (jerarquiaNodes.length === 0) {
            setJerarquiaNodesLoading(true);
            try {
                const response = await getNodes();
                const nodes = Array.isArray(response?.data)
                    ? response.data
                    : response?.data?.results || [];
                setJerarquiaNodes(nodes.filter(n => n.level_nombre === 'localización'));
            }
            catch {
                setJerarquiaNodes([]);
            }
            finally {
                setJerarquiaNodesLoading(false);
            }
        }
    };

    const handleCloseJerarquiaSelector = () => {
        setJerarquiaSelectorOpen(false);
        setJerarquiaSelectorBill(null);
        setJerarquiaSearch('');
    };

    const handleSelectJerarquia = async node => {
        if (!jerarquiaSelectorBill || !node?.id) return;
        const bill = jerarquiaSelectorBill;
        handleCloseJerarquiaSelector();
        setLinkingBill({ id: bill.id, tipo: bill.tipo });
        try {
            await linkBill(bill.tipo, bill.id, node.id);
            await fetchOrphanSummary();
            await fetchBillsList();
        }
        catch {
            // keep state, user can retry
        }
        finally {
            setLinkingBill(null);
        }
    };

    const handleOpenServicesDialog = async () => {
        setServicesDialogOpen(true);
        setServicesStep('select');
        setServicesNode(null);
        setServicesNodeSearch('');
        setServicesNodesLoading(true);
        try {
            const response = await getNodes();
            const nodes = Array.isArray(response?.data)
                ? response.data
                : response?.data?.results || [];
            setServicesNodes(nodes.filter(n => n.level_nombre === 'localización'));
        } catch {
            setServicesNodes([]);
        } finally {
            setServicesNodesLoading(false);
        }
    };

    const handleCloseServicesDialog = () => {
        setServicesDialogOpen(false);
        setServicesNode(null);
        setServicesNodeSearch('');
        setServicesStep('select');
        setElectricityService({ ...emptyService });
        setWaterService({ ...emptyService });
        setGasService({ ...emptyService });
    };

    const handleSelectServicesNode = async node => {
        setServicesNode(node);
        setServicesStep('services');
        setServicesLoading(true);
        setElectricityService({ ...emptyService });
        setWaterService({ ...emptyService });
        setGasService({ ...emptyService });
        try {
            const [servicesRes, metersRes] = await Promise.all([
                getLocationServices(node.id),
                getUnassignedMeters(),
            ]);
            const raw = servicesRes.data;
            const configs = Array.isArray(raw?.results) ? raw.results : Array.isArray(raw) ? raw : [];
            setElectricityService(configs.find(c => c.tipo_servicio === 'electricidad') || { ...emptyService });
            setWaterService(configs.find(c => c.tipo_servicio === 'agua') || { ...emptyService });
            setGasService(configs.find(c => c.tipo_servicio === 'gas') || { ...emptyService });
            setUnassignedMeters(Array.isArray(metersRes.data) ? metersRes.data : metersRes.data?.results || []);
        } catch (error) {
            console.error('Error cargando servicios para nodo', node.id, error);
            setServicesNotification({ open: true, message: 'Error al cargar los servicios de la localización', severity: 'error' });
        } finally {
            setServicesLoading(false);
        }
    };

    const handleSaveService = async tipo => {
        const serviceMap = {
            electricidad: { data: electricityService, setter: setElectricityService },
            agua: { data: waterService, setter: setWaterService },
            gas: { data: gasService, setter: setGasService },
        };
        const { data, setter } = serviceMap[tipo];
        const payload = {
            jerarquia: servicesNode.id,
            tipo_servicio: tipo,
            id_cliente_utility: data.id_cliente_utility,
            id_medidor_utility: data.id_medidor_utility,
            medidor: data.medidor || null,
        };
        try {
            let res;
            if (data.id) {
                res = await updateLocationService(data.id, payload);
            } else {
                res = await createLocationService(payload);
            }
            setter(res.data);
            setServicesNotification({ open: true, message: 'Sección guardada correctamente', severity: 'success' });
        } catch (error) {
            const apiMsg = error?.response?.data?.id_cliente_utility
                || error?.response?.data?.non_field_errors
                || 'Error al guardar la sección';
            setServicesNotification({ open: true, message: Array.isArray(apiMsg) ? apiMsg[0] : apiMsg, severity: 'error' });
        }
    };

    const handleClearService = async tipo => {
        const serviceMap = {
            electricidad: { data: electricityService, setter: setElectricityService },
            agua: { data: waterService, setter: setWaterService },
            gas: { data: gasService, setter: setGasService },
        };
        const { data, setter } = serviceMap[tipo];
        try {
            if (data.id) {
                await deleteLocationService(data.id);
            }
            setter({ ...emptyService });
            setServicesNotification({ open: true, message: 'Sección eliminada', severity: 'success' });
        } catch {
            setServicesNotification({ open: true, message: 'Error al eliminar la sección', severity: 'error' });
        }
    };

    const renderServiceCard = (tipo, label, color, icon, data, setter) => {
        const configured = !!data.id;
        const meterOptions = data.medidor && !unassignedMeters?.find(m => m.id === data.medidor)
            ? [{ id: data.medidor, nombre: data.medidor_llave || `Medidor ${data.medidor}` }, ...(unassignedMeters || [])]
            : unassignedMeters || [];
        return (
            <Box className={classes.serviceCard} key={tipo}>
                <Box className={classes.serviceCardHeader} style={{ backgroundColor: color }}>
                    <Box className={classes.serviceCardHeaderLeft}>
                        {icon}
                        <span>{label}</span>
                    </Box>
                    <Box className={classes.serviceCardBadge}>
                        {configured
                            ? <><CheckCircle style={{ fontSize: 14 }} /> Configurado</>
                            : <><RadioButtonUnchecked style={{ fontSize: 14 }} /> No configurado</>
                        }
                    </Box>
                </Box>
                <Box className={classes.serviceCardBody}>
                    <Box className={classes.serviceLabel}>ID Cliente</Box>
                    <TextField
                        fullWidth
                        size='small'
                        variant='outlined'
                        placeholder='ID Cliente'
                        value={data.id_cliente_utility}
                        onChange={e => setter({ ...data, id_cliente_utility: e.target.value })}
                    />
                    <Box className={classes.serviceLabel} style={{ marginTop: 10 }}>
                        {tipo === 'electricidad' ? 'ID Sitio / Punto de suministro (según factura)' : 'ID Suministro'}
                    </Box>
                    <TextField
                        fullWidth
                        size='small'
                        variant='outlined'
                        placeholder={tipo === 'electricidad' ? 'ID Sitio' : 'ID Suministro'}
                        value={data.id_medidor_utility}
                        onChange={e => setter({ ...data, id_medidor_utility: e.target.value })}
                    />
                    <Box className={classes.serviceLabel} style={{ marginTop: 10 }}>
                        {tipo === 'electricidad' ? 'Medidor de electricidad asociado' : tipo === 'agua' ? 'Medidor de agua asociado' : 'Medidor de gas asociado'}
                    </Box>
                    <FormControl fullWidth variant='outlined' size='small'>
                        <Select
                            displayEmpty
                            value={data.medidor || ''}
                            onChange={e => setter({ ...data, medidor: e.target.value || null })}
                        >
                            <MenuItem value=''><em>Sin medidor</em></MenuItem>
                            {meterOptions.map(m => (
                                <MenuItem key={m.id} value={m.id}>{m.nombre || m.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box className={classes.serviceCardFooter}>
                    <Button size='small' color='primary' variant='contained' onClick={() => handleSaveService(tipo)}>
                        Guardar sección
                    </Button>
                    <Button size='small' variant='outlined' onClick={() => handleClearService(tipo)}>
                        Limpiar
                    </Button>
                </Box>
            </Box>
        );
    };

    const pollJobProgress = async recordId => {
        try {
            const response = await getBillProcessingProgress(recordId);
            const normalizedRecord = normalizeProcessingRecord(response?.data);

            mergeUploadJobs([normalizedRecord]);

            if (normalizedRecord.estado !== 'PROCESSING') {
                stopPollingJob(recordId);

                if (normalizedRecord.estado === 'OK') {
                    fetchBillsList();
                }
            }
        }
        catch (error) {
            stopPollingJob(recordId);
            setUploadFeedback({
                type: 'error',
                message: 'No se pudo actualizar el progreso de uno de los archivos cargados.',
            });
        }
    };

    const startPollingJob = recordId => {
        if (!recordId || pollingIntervalsRef.current[recordId]) {
            return;
        }

        pollJobProgress(recordId);
        pollingIntervalsRef.current[recordId] = setInterval(() => {
            pollJobProgress(recordId);
        }, 2000);
    };

    const fetchProcessingHistory = async () => {
        setLoadingUploadHistory(true);
        try {
            const response = await getBillProcessingHistory({
                page: 1,
                page_size: 50,
            });
            const responseData = response?.data;
            const items = Array.isArray(responseData)
                ? responseData
                : responseData?.results || responseData?.data || responseData?.items || [];
            const normalizedItems = items.map(normalizeProcessingRecord);

            mergeUploadJobs(normalizedItems);
            const processingOnMount = normalizedItems.filter(job => job.estado === 'PROCESSING');
            processingOnMount.forEach(job => startPollingJob(job.id));

            if (processingOnMount.length > 0) {
                processingOnMount.forEach(job => {
                    prevUploadJobStatesRef.current[job.id] = 'PROCESSING';
                });
                setUploadProgress(p =>
                    p.phase === 'idle'
                        ? { phase: 'processing', completedUploads: 0, totalUploads: 0, completedJobs: 0, totalJobs: processingOnMount.length }
                        : p
                );
            }
        }
        catch (error) {
            setUploadFeedback({
                type: 'error',
                message: 'No se pudo recuperar el historial de archivos en procesamiento.',
            });
        }
        finally {
            setLoadingUploadHistory(false);
        }
    };

    useEffect(() => {
        fetchBillsList();
        // fetchBillsList depende del filtro actual y queremos refrescar solo cuando cambian esos valores.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        filters.dateFrom,
        filters.dateTo,
        filters.invoiceNumber,
        filters.provider,
        filters.serviceType,
        filters.status,
        page,
        rowsPerPage,
    ]);

    useEffect(() => {
        fetchOrphanSummary();
        fetchPendingRevisionBills();
    }, []);

    useEffect(() => {
        fetchProcessingHistory();
        patchVerificacionAutomatica({ habilitada: false }).catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (uploadModalOpen) {
            fetchProcessingHistory();
        }
        // fetchProcessingHistory se usa al abrir el modal para retomar jobs activos.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadModalOpen]);

    useEffect(() => {
        setPage(0);
    }, [filters.invoiceNumber, filters.provider, filters.serviceType, filters.status, filters.dateFrom, filters.dateTo]);

    useEffect(() => () => {
        Object.values(pollingIntervalsRef.current).forEach(intervalId => clearInterval(intervalId));
    }, []);

    useEffect(() => {
        if (uploadProgress.phase !== 'processing') {
            return;
        }

        let newlyCompleted = 0;
        uploadJobs.forEach(job => {
            const prev = prevUploadJobStatesRef.current[job.id];
            if (prev === 'PROCESSING' && job.estado !== 'PROCESSING') {
                newlyCompleted += 1;
                prevUploadJobStatesRef.current[job.id] = job.estado;
            }
        });

        if (newlyCompleted === 0) {
            return;
        }

        fetchPendingRevisionBills();

        setUploadProgress(p => {
            const completedJobs = p.completedJobs + newlyCompleted;
            const allDone = completedJobs >= p.totalJobs;
            return { ...p, completedJobs, phase: allDone ? 'done' : 'processing' };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadJobs]);

    useEffect(() => {
        if (uploadProgress.phase !== 'done') {
            return;
        }
        const timer = setTimeout(
            () => setUploadProgress({ phase: 'idle', completedUploads: 0, totalUploads: 0, completedJobs: 0, totalJobs: 0 }),
            3000
        );
        return () => clearTimeout(timer);
    }, [uploadProgress.phase]);

    const exportMenuOpen = Boolean(exportAnchorEl);
    const okBills = bills.filter(bill => bill.status === 'OK').length;
    const processingJobsCount = uploadJobs.filter(job => job.estado === 'PROCESSING').length;
    const issueBills = bills.filter(bill => bill.status === 'ERROR' || bill.status === 'ABORTADO').length;
    const totalAmount = totalBillsAmount;

    const handleOpenExportMenu = event => {
        setExportAnchorEl(event.currentTarget);
    };

    const handleCloseExportMenu = () => {
        setExportAnchorEl(null);
    };

    const handleOpenRowActionsMenu = (event, bill) => {
        setRowActionsAnchorEl(event.currentTarget);
        setSelectedRowBill(bill);
    };

    const handleCloseRowActionsMenu = () => {
        setRowActionsAnchorEl(null);
        setSelectedRowBill(null);
    };

    const handleEditRowBill = () => {
        if (selectedRowBill) {
            handleOpenBillDetail(selectedRowBill);
        }
        handleCloseRowActionsMenu();
    };

    const handleDeleteRowBillRequest = () => {
        if (selectedRowBill) {
            setBillToDelete(selectedRowBill);
            setDeleteConfirmOpen(true);
        }
        handleCloseRowActionsMenu();
    };

    const handleConfirmDelete = async () => {
        if (!billToDelete) return;
        setDeletingBill(true);
        setDeleteError('');
        try {
            await deleteBill(billToDelete.tipo, billToDelete.id);
            setBills(current => current.filter(b => b.id !== billToDelete.id));
            setTotalBillsCount(count => Math.max(0, count - 1));
            setDeleteConfirmOpen(false);
            setBillToDelete(null);
        }
        catch (error) {
            const status = error?.response?.status;
            if (status === 404) {
                setDeleteError('La factura no fue encontrada o ya fue eliminada.');
            }
            else {
                setDeleteError('No se pudo eliminar la factura. Intentá de nuevo.');
            }
        }
        finally {
            setDeletingBill(false);
        }
    };

    const handleCancelDelete = () => {
        if (deletingBill) return;
        setDeleteConfirmOpen(false);
        setBillToDelete(null);
        setDeleteError('');
    };

    const handleViewRowBill = async () => {
        if (!selectedRowBill?.archivoUrl) {
            handleCloseRowActionsMenu();
            return;
        }

        try {
            await handleDownloadBillDocument(selectedRowBill);
        }
        catch (error) {
            // El handler compartido ya informa el error.
        }
        finally {
            handleCloseRowActionsMenu();
        }
    };

    const handleDownloadBillDocument = async billFile => {
        if (!billFile?.archivoUrl) {
            return;
        }

        try {
            const response = await downloadBillFile(billFile.archivoUrl);
            const disposition = response?.headers?.['content-disposition'] || response?.headers?.['Content-Disposition'];
            const filename =
                getFilenameFromDisposition(disposition) ||
                billFile?.archivoNombre ||
                billFile?.code ||
                'factura.pdf';
            const downloadUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');

            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        }
        catch (error) {
            setUploadFeedback({
                type: 'error',
                message: 'No se pudo descargar la factura seleccionada.',
            });
        }
    };

    const history = useHistory();
    const isUploadBlocked = billsQuota?.sin_cupo_configurado === true;

    const handleOpenUploadModal = () => {
        setUploadModalOpen(true);
        setUploadStep(uploadProgress.phase !== 'idle' ? 'files' : 'options');
        setUploadOption(isUploadBlocked ? 'manual' : 'files');
        setIsDragActive(false);
        setUploadFeedback({ type: '', message: '' });
    };

    const handleOpenUploadTracking = () => {
        setUploadModalOpen(true);
        setUploadStep('files');
        setUploadOption('files');
        setIsDragActive(false);
        setUploadFeedback({ type: '', message: '' });
    };

    const handleCloseUploadModal = () => {
        setUploadModalOpen(false);
        setUploadStep('options');
        setIsDragActive(false);
        setUploadFeedback({ type: '', message: '' });
    };

    const appendFiles = filesList => {
        const nextFiles = Array.from(filesList || []);
        if (nextFiles.length === 0) {
            return;
        }

        setSelectedFiles(currentFiles => [...currentFiles, ...nextFiles]);
        setUploadFeedback({ type: '', message: '' });
    };

    const handleContinueUpload = (nextUploadOption = uploadOption) => {
        setUploadOption(nextUploadOption);

        if (nextUploadOption === 'files') {
            setUploadStep('files');
            return;
        }

        setUploadModalOpen(false);
        setUploadStep('options');
        setSelectedFiles([]);
        setEditingBillRow(null);
        setDetailMode('create');
        setSelectedBillDetail(createEmptyManualBill());
        setDetailLoading(false);
        setDetailModalOpen(true);
    };

    const handleBackUploadStep = () => {
        setUploadStep('options');
    };

    const handleRemoveSelectedFile = fileIndex => {
        setSelectedFiles(currentFiles => currentFiles.filter((_, index) => index !== fileIndex));
    };

    const handleSubmitFiles = async () => {
        if (selectedFiles.length === 0) {
            setUploadFeedback({
                type: 'error',
                message: 'Selecciona al menos un archivo antes de iniciar el procesamiento.',
            });
            return;
        }

        setSubmittingFiles(true);
        setUploadFeedback({ type: '', message: '' });

        const filesToUpload = [...selectedFiles];
        const totalUploads = filesToUpload.length;
        let completedUploads = 0;
        prevUploadJobStatesRef.current = {};
        setUploadProgress({ phase: 'uploading', completedUploads: 0, totalUploads, completedJobs: 0, totalJobs: 0 });

        const uploadResults = await Promise.all(
            filesToUpload.map(file =>
                uploadBillFile(file).then(
                    value => {
                        completedUploads += 1;
                        setUploadProgress(p => ({ ...p, completedUploads }));
                        return { status: 'fulfilled', value };
                    },
                    reason => {
                        completedUploads += 1;
                        setUploadProgress(p => ({ ...p, completedUploads }));
                        return { status: 'rejected', reason };
                    }
                )
            )
        );
        const jobsToMerge = [];
        let successfulUploads = 0;
        let failedUploads = 0;
        let duplicateUploads = 0;

        uploadResults.forEach((result, index) => {
            const originalFile = filesToUpload[index];

            if (result.status === 'fulfilled') {
                successfulUploads += 1;
                const data = result.value?.data || {};
                const recordIds = Array.isArray(data.record_ids) ? data.record_ids : [];
                const immediateResults = Array.isArray(data.immediate_results) ? data.immediate_results : [];

                if (immediateResults.length > 0) {
                    duplicateUploads += 1;
                }

                immediateResults.forEach(item => {
                    const normalizedImmediate = normalizeProcessingRecord({
                        ...item,
                        filename: item?.filename || originalFile?.name,
                        estado: item?.estado || 'OK',
                    });
                    jobsToMerge.push(normalizedImmediate);
                });

                recordIds.forEach(recordId => {
                    jobsToMerge.push(normalizeProcessingRecord({
                        id: recordId,
                        filename: originalFile?.name,
                        estado: 'PROCESSING',
                        batch_id: data.batch_id || null,
                        celery_task_id: Array.isArray(data.task_ids) ? data.task_ids[0] : null,
                        progreso: {
                            step: 'creating_record',
                            message: 'Archivo recibido. Iniciando procesamiento...',
                        },
                    }));
                });
            }
            else {
                failedUploads += 1;
                const errorMessage =
                    result.reason?.response?.data?.file?.[0] ||
                    result.reason?.response?.data?.detail ||
                    'No se pudo subir el archivo.';

                jobsToMerge.push(normalizeProcessingRecord({
                    id: `error-${Date.now()}-${index}`,
                    filename: originalFile?.name,
                    estado: 'ERROR',
                    error_message: errorMessage,
                }));
            }
        });

        mergeUploadJobs(jobsToMerge);
        jobsToMerge
            .filter(job => job.estado === 'PROCESSING' && Number.isFinite(Number(job.id)))
            .forEach(job => startPollingJob(job.id));

        setSelectedFiles([]);
        setSubmittingFiles(false);

        const processingJobs = jobsToMerge.filter(job => job.estado === 'PROCESSING');
        const totalJobs = processingJobs.length;

        if (totalJobs > 0) {
            processingJobs.forEach(job => {
                prevUploadJobStatesRef.current[job.id] = 'PROCESSING';
            });
            setUploadProgress(p => ({ ...p, phase: 'processing', totalJobs, completedJobs: 0 }));
        }
        else {
            setUploadProgress(p => ({ ...p, phase: 'done' }));
        }

        if (failedUploads > 0 && successfulUploads === 0) {
            setUploadFeedback({
                type: 'error',
                message: 'No se pudo iniciar el procesamiento de los archivos seleccionados.',
            });
            return;
        }

        if (failedUploads > 0) {
            setUploadFeedback({
                type: 'error',
                message: 'Algunos archivos se enviaron correctamente y otros fallaron. Revisa el detalle debajo.',
            });
            return;
        }

        if (duplicateUploads > 0 && successfulUploads === duplicateUploads) {
            setUploadFeedback({
                type: 'warning',
                message: 'Los archivos seleccionados ya habian sido cargados anteriormente.',
            });
            return;
        }

        if (duplicateUploads > 0) {
            setUploadFeedback({
                type: 'warning',
                message: 'Algunos archivos ya habian sido cargados anteriormente. El resto esta siendo procesado.',
            });
            return;
        }

        setUploadFeedback({
            type: 'success',
            message: 'Los archivos fueron enviados y el procesamiento ya esta en curso.',
        });
    };

    const handleAbortJob = async recordId => {
        try {
            await abortBillProcessing(recordId);
            mergeUploadJobs([{ id: recordId, estado: 'ABORTADO', progressMessage: 'Procesamiento cancelado por el usuario.' }]);
            stopPollingJob(recordId);
        }
        catch (error) {
            if (error?.response?.status === 409) {
                pollJobProgress(recordId);
                return;
            }

            setUploadFeedback({
                type: 'error',
                message: 'No se pudo abortar el procesamiento seleccionado.',
            });
        }
    };

    const handleRetryJob = async recordId => {
        try {
            const response = await retryBillProcessing(recordId);
            mergeUploadJobs([normalizeProcessingRecord({
                id: response?.data?.id || recordId,
                estado: 'PROCESSING',
                celery_task_id: response?.data?.celery_task_id || null,
                progreso: {
                    step: 'creating_record',
                    message: 'Reintentando procesamiento...',
                },
            })]);
            startPollingJob(recordId);
        }
        catch (error) {
            setUploadFeedback({
                type: 'error',
                message: 'No se pudo reintentar el archivo seleccionado.',
            });
        }
    };

    const handleUploadInputChange = event => {
        appendFiles(event.target.files);
        event.target.value = '';
    };

    const handleDragOver = event => {
        event.preventDefault();
        setIsDragActive(true);
    };

    const handleDragLeave = event => {
        event.preventDefault();
        setIsDragActive(false);
    };

    const handleDropFiles = event => {
        event.preventDefault();
        setIsDragActive(false);
        appendFiles(event.dataTransfer.files);
    };

    const handleFilterChange = event => {
        const { name, value } = event.target;
        setPage(0);
        setFilters(currentFilters => ({
            ...currentFilters,
            [name]: value,
        }));
    };

    const handleClearFilters = () => {
        setFilters(initialFilters);
        setPage(0);
    };

    const handleChangePage = (_, nextPage) => {
        setPage(nextPage);
    };

    const handleChangeRowsPerPage = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleExportCsv = async () => {
        try {
            const response = await exportBillsCsv({
                tipo_servicio: filters.serviceType || '',
                estado: filters.status || '',
                proveedor: filters.provider || '',
                fecha_emision_desde: filters.dateFrom || '',
                fecha_emision_hasta: filters.dateTo || '',
                numero_factura: filters.invoiceNumber || '',
                page: 1,
                page_size: 5000,
            });

            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = 'facturas.csv';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        }
        catch (error) {
            setUploadFeedback({
                type: 'error',
                message: 'No se pudo exportar el listado de facturas a CSV.',
            });
        }
        finally {
            handleCloseExportMenu();
        }
    };

    const handleOpenBillDetail = async bill => {
        if (!bill?.id) {
            return;
        }

        const fallbackBillDetail = buildFallbackBillDetail(bill);
        setEditingBillRow(bill);
        setDetailMode('edit');
        setDetailModalOpen(true);
        setDetailLoading(true);
        setSelectedBillDetail(fallbackBillDetail);

        try {
            const response = await getBillDetail(bill.tipo || 'electricidad', bill.id);
            setSelectedBillDetail({
                ...fallbackBillDetail,
                ...(response?.data || {}),
            });
        }
        catch (error) {
            setSelectedBillDetail(fallbackBillDetail);
        }
        finally {
            setDetailLoading(false);
        }
    };

    const handleCloseBillDetail = (options = {}) => {
        const { returnToUploadModal = detailReturnToUploadModal } = options;

        setDetailModalOpen(false);
        setSelectedBillDetail(null);
        setEditingBillRow(null);
        setDetailMode('view');
        setDetailReturnToUploadModal(false);

        if (returnToUploadModal) {
            setUploadModalOpen(true);
            setUploadStep('files');
            setUploadOption('files');
            setIsDragActive(false);
        }
    };

    const handleSaveBillEditor = async formData => {
        if (detailMode === 'create') {
            const payload = buildManualBillPayload(formData);
            await createManualBill(payload);
            handleCloseBillDetail();
            fetchBillsList();
            return;
        }

        const nextBillRow = buildBillRowFromEditorForm(formData, editingBillRow);
        const nextBillDetail = applyEditorFormToBillDetail(selectedBillDetail, formData);
        setBills(currentBills =>
            currentBills.map(currentBill =>
                currentBill.id === editingBillRow?.id ? nextBillRow : currentBill
            )
        );
        setSelectedBillDetail(nextBillDetail);
        fetchBillsList();
    };

    const handleRefreshBillDetail = async () => {
        if (!editingBillRow?.id) return;
        try {
            const response = await getBillDetail(editingBillRow.tipo || 'electricidad', editingBillRow.id);
            setSelectedBillDetail(prev => ({
                ...(prev || {}),
                ...(response?.data || {}),
            }));
        } catch (e) {
            // ignore, keep current data
        }
    };

    const handleDeleteBillEditor = () => {
        if (detailMode === 'create') {
            handleCloseBillDetail({ returnToUploadModal: false });
            return;
        }

        handleCloseBillDetail({ returnToUploadModal: false });
        setBillToDelete(editingBillRow);
        setDeleteConfirmOpen(true);
    };

    const handleOpenProcessedJobDetail = async job => {
        if (!job) {
            return;
        }

        const existingBillRow = bills.find(currentBill => currentBill.id === job.billId || currentBill.id === job.id);
        setDetailReturnToUploadModal(uploadModalOpen);
        setUploadModalOpen(false);
        setDetailMode('edit');
        setEditingBillRow(existingBillRow || buildBillRowFromProcessedJob(job));
        setDetailModalOpen(true);
        setDetailLoading(true);

        const fallbackDetail = {
            ...createEmptyManualBill(),
            tipo: job.billTipo || 'electricidad',
            archivo_url: job.archivoUrl || '',
            archivo_nombre: job.filename || '',
            score: job.score ?? '',
            ...(job.billData && typeof job.billData === 'object' ? job.billData : {}),
        };

        setSelectedBillDetail(fallbackDetail);

        const billIdToFetch = job.billId || job.billData?.id;
        const billTipoToFetch = job.billData?.factura_tipo || job.billData?.tipo_servicio || job.billTipo || 'electricidad';

        if (billIdToFetch) {
            try {
                const response = await getBillDetail(billTipoToFetch, billIdToFetch);
                setSelectedBillDetail({
                    ...fallbackDetail,
                    ...(response?.data || {}),
                });
            }
            catch (error) {
                setSelectedBillDetail(fallbackDetail);
            }
            finally {
                setDetailLoading(false);
            }
            return;
        }

        setDetailLoading(false);
    };

    const handleSetProcessedJobDecision = async (processedJobId, decision) => {
        const bill = pendingRevisionBills.find(
            b => `pending-${b.tipo}-${b.id}` === processedJobId
        );
        setSelectedProcessedJobIds(currentIds => currentIds.filter(id => id !== processedJobId));

        if (!bill) {
            return;
        }

        if (decision === 'accepted') {
            try {
                await markBillsAsReviewed([{ tipo: bill.tipo, id: bill.id }], true);
                fetchBillsList();
            }
            catch {
                // Si falla la API igual lo quitamos del panel para no bloquear al usuario.
            }
        }
        else if (decision === 'rejected') {
            try {
                await deleteBill(bill.tipo, bill.id);
            }
            catch {
                // Igual lo quitamos si falla el borrado.
            }
        }

        setPendingRevisionBills(current => current.filter(b => b.id !== bill.id || b.tipo !== bill.tipo));
    };

    const handleToggleProcessedJobSelection = processedJobId => {
        setSelectedProcessedJobIds(currentIds =>
            currentIds.includes(processedJobId)
                ? currentIds.filter(currentId => currentId !== processedJobId)
                : [...currentIds, processedJobId]
        );
    };

    const handleToggleAllProcessedJobSelections = () => {
        setSelectedProcessedJobIds(currentIds =>
            currentIds.length === processedReviewJobs.length ? [] : processedReviewJobs.map(item => item.id)
        );
    };

    const handleApplyBulkProcessedJobDecision = decision => {
        if (selectedProcessedJobIds.length === 0) {
            return;
        }

        selectedProcessedJobIds.forEach(processedJobId => {
            handleSetProcessedJobDecision(processedJobId, decision);
        });
        setSelectedProcessedJobIds([]);
    };

    const filteredBills = bills;
    const paginatedBills = filteredBills;
    const processedReviewJobs = pendingRevisionBills.map(bill => ({
        id: `pending-${bill.tipo}-${bill.id}`,
        filename: bill.code,
        estado: 'OK',
        score: bill.score,
        humanRevision: false,
        billId: bill.id,
        billTipo: bill.tipo,
    }));
    const processingOnlyJobs = uploadJobs.filter(job => job.estado === 'PROCESSING');
    const allProcessedJobsSelected =
        processedReviewJobs.length > 0 && selectedProcessedJobIds.length === processedReviewJobs.length;
    const someProcessedJobsSelected =
        selectedProcessedJobIds.length > 0 && selectedProcessedJobIds.length < processedReviewJobs.length;
    const renderUploadJobCard = (job, options = {}) => {
        const statusConfig = getUploadStatusConfig(theme, job.estado);
        const {
            extraActions = null,
            hideScore = false,
            hideProgress = false,
            actionsBelowStatus = false,
            leadingControl = null,
            statusLabel = null,
            statusStyle = null,
            compact = false,
        } = options;

        if (compact) {
            return (
                <Box key={job.id} className={classes.compactUploadJobCard}>
                    <Box className={classes.compactJobRow}>
                        {leadingControl}
                        <Typography className={classes.compactJobFilename}>
                            {job.filename}
                        </Typography>
                        {!hideScore && job.score !== null && job.score !== undefined && (
                            <Tooltip title='Precisión de extracción automática'>
                                <Box
                                    className={classes.scoreChip}
                                    style={
                                        job.score >= 86
                                            ? { backgroundColor: '#dcfce7', color: '#166534' }
                                            : job.score >= 60
                                                ? { backgroundColor: '#fef9c3', color: '#854d0e' }
                                                : { backgroundColor: '#fee2e2', color: '#991b1b' }
                                    }
                                >
                                    {job.score}%
                                </Box>
                            </Tooltip>
                        )}
                        <Box
                            className={classes.uploadJobStatus}
                            style={{
                                backgroundColor: statusStyle?.backgroundColor || statusConfig.backgroundColor,
                                color: statusStyle?.color || statusConfig.color,
                            }}
                        >
                            {statusLabel || statusConfig.label}
                        </Box>
                        {extraActions}
                    </Box>
                </Box>
            );
        }

        return (
            <Box key={job.id} className={classes.uploadJobCard}>
                <Box className={classes.uploadJobHeader}>
                    <Box className={classes.uploadJobHeaderMain}>
                        {leadingControl}
                        <Box className={classes.uploadJobInfo}>
                            <Typography className={classes.uploadJobName}>
                                {job.filename}
                            </Typography>
                            <Typography className={classes.uploadJobMeta}>
                                {!hideScore && job.score !== null && job.score !== undefined && (
                                    <>
                                        {' · '}
                                        <Tooltip title='Precisión de extracción automática'>
                                            <Box
                                                component='span'
                                                className={classes.scoreChip}
                                                style={
                                                    job.score >= 86
                                                        ? { backgroundColor: '#dcfce7', color: '#166534' }
                                                        : job.score >= 60
                                                            ? { backgroundColor: '#fef9c3', color: '#854d0e' }
                                                            : { backgroundColor: '#fee2e2', color: '#991b1b' }
                                                }
                                            >
                                                {job.score}%
                                            </Box>
                                        </Tooltip>
                                    </>
                                )}
                            </Typography>
                        </Box>
                    </Box>

                    <Box className={classes.uploadJobAside}>
                        <Box
                            className={classes.uploadJobStatus}
                            style={{
                                backgroundColor: statusStyle?.backgroundColor || statusConfig.backgroundColor,
                                color: statusStyle?.color || statusConfig.color,
                            }}
                        >
                            {statusLabel || statusConfig.label}
                        </Box>
                        {actionsBelowStatus ? extraActions : null}
                    </Box>
                </Box>

                {!hideProgress && job.estado === 'PROCESSING' ? (
                    <Box className={classes.uploadJobProgress}>
                        <CircularProgress size={14} thickness={4} />
                    </Box>
                ) : !hideProgress && (job.progressMessage || job.errorMessage) ? (
                    <Typography className={classes.uploadJobProgress}>
                        {job.errorMessage || job.progressMessage}
                    </Typography>
                ) : null}

                {!actionsBelowStatus && <Box className={classes.uploadJobActions}>
                    {job.estado === 'OK' && (
                        <Button
                            size='small'
                            className={classes.uploadActionButton}
                            startIcon={<VisibilityOutlinedIcon />}
                            onClick={() => handleOpenProcessedJobDetail(job)}
                        >
                            Ver factura
                        </Button>
                    )}

                    {job.estado === 'PROCESSING' && (
                        <Button
                            size='small'
                            className={classes.uploadActionButton}
                            startIcon={<StopOutlinedIcon />}
                            onClick={() => handleAbortJob(job.id)}
                        >
                            Abortar
                        </Button>
                    )}

                    {(job.estado === 'ERROR' || job.estado === 'ABORTADO') &&
                        Number.isFinite(Number(job.id)) && (
                            <Button
                                size='small'
                                className={classes.uploadActionButton}
                                startIcon={<ReplayOutlinedIcon />}
                                onClick={() => handleRetryJob(job.id)}
                            >
                                Reintentar
                            </Button>
                        )}

                    {job.archivoUrl && (
                        <Button
                            size='small'
                            className={classes.uploadActionButton}
                            startIcon={<GetAppOutlinedIcon />}
                            component='a'
                            href={job.archivoUrl}
                            target='_blank'
                            rel='noreferrer'
                        >
                            Descargar original
                        </Button>
                    )}

                    {extraActions}
                </Box>}
            </Box>
        );
    };

    return (
        <>
            <TopNav titulo='Facturación' subtitulo='| Gestión de facturas'>
                <UsageIndicator
                    label="Facturas"
                    used={billsQuota?.consumido ?? 0}
                    total={billsQuota?.sin_cupo_configurado ? null : (billsQuota?.capacidad ?? null)}
                />
                <Box className={classes.topActions} style={{ marginLeft: '0.75rem' }}>
                    {pendingRevisionBills.length > 0 ? (
                        <Button
                            variant='contained'
                            disableElevation
                            startIcon={<DoneOutlinedIcon />}
                            className={classes.primaryActionButton}
                            onClick={handleOpenUploadTracking}
                        >
                            <Box className={classes.processingButtonContent}>
                                <span>Pendientes de revisión</span>
                                <Box component='span' className={classes.processingBadge}>
                                    {pendingRevisionBills.length}
                                </Box>
                            </Box>
                        </Button>
                    ) : processingOnlyJobs.length > 0 ? (
                        <Button
                            className={classes.secondaryTopActionButton}
                            startIcon={<VisibilityOutlinedIcon />}
                            onClick={handleOpenUploadTracking}
                        >
                            <Box className={classes.processingButtonContent}>
                                <span>Facturas en procesamiento</span>
                                <Box component='span' className={classes.processingBadge}>
                                    {processingJobsCount}
                                </Box>
                            </Box>
                        </Button>
                    ) : (
                        <Button
                            variant='contained'
                            disableElevation
                            startIcon={<CloudUploadOutlinedIcon />}
                            className={classes.primaryActionButton}
                            onClick={handleOpenUploadModal}
                        >
                            Cargar Facturas
                        </Button>
                    )}

                    <Button
                        className={classes.exportButton}
                        startIcon={<TuneIcon />}
                        onClick={handleOpenServicesDialog}
                        style={{ padding: '0 12px' }}
                    >
                        <Box className={classes.exportLabel}>Gestionar Servicios</Box>
                    </Button>

                    <Button className={classes.exportButton} onClick={handleOpenExportMenu}>
                        <Box className={classes.exportLabel}>Exportar Datos</Box>
                        <IconButton className={classes.exportArrow}>
                            <KeyboardArrowDownIcon />
                        </IconButton>
                    </Button>
                </Box>
            </TopNav>
            <Box className={classes.root}>
                <Container>
                    <Paper elevation={0} className={classes.header}>
                        <Grid container spacing={3} alignItems='center' justifyContent='space-between'>
                            <Grid item xs={12} md={5}>
                                <Box display='flex' alignItems='center' className={classes.titleRow}>
                                    <Box className={classes.titleIcon}>
                                        <ReceiptOutlinedIcon fontSize='large' />
                                    </Box>
                                    <Box>
                                        <Typography variant='h6' className={classes.title}>
                                            Listado de facturas
                                        </Typography>
                                        <Typography className={classes.subtitle}>
                                            A continuación podrá ver el listado de facturas cargadas.
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            {/* <Grid item xs={6} sm={4} md={2}>
                                <Box className={`${classes.summaryCard} ${classes.summaryCardGreen}`}>
                                    <Typography className={classes.summaryLabel}>Procesadas</Typography>
                                    <Typography className={classes.summaryValue}>{okBills}</Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={6} sm={4} md={2}>
                                <Box className={`${classes.summaryCard} ${classes.summaryCardRed}`}>
                                    <Typography className={classes.summaryLabel}>Con errores</Typography>
                                    <Typography className={classes.summaryValue}>{issueBills}</Typography>
                                </Box>
                            </Grid> */}

                            <Grid item xs={12} sm={4} md={3}>
                                <Box className={classes.summaryCard}>
                                    <Typography className={classes.summaryLabel}>Monto total</Typography>
                                    <Typography className={classes.summaryValue}>
                                        {currencyFormatter.format(totalAmount)}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>

                    {orphanSummary.count > 0 && (
                        <Box className={classes.orphanBanner} onClick={handleOpenOrphanDialog}>
                            <RemoveCircleOutlineOutlinedIcon fontSize='small' />
                            <Typography className={classes.orphanBannerText}>
                                Hay <strong>{orphanSummary.count}</strong> factura{orphanSummary.count !== 1 ? 's' : ''} sin vincular a una localización. Revisalas para completar la carga.
                            </Typography>
                            <Typography className={classes.orphanBannerAction}>Revisar</Typography>
                        </Box>
                    )}

                    <Paper elevation={0} className={classes.filtersPanel}>
                        <Grid container spacing={1}>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    fullWidth
                                    size='small'
                                    variant='outlined'
                                    label='Numero de factura'
                                    name='invoiceNumber'
                                    value={filters.invoiceNumber}
                                    onChange={handleFilterChange}
                                    className={classes.filterField}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <SearchIcon color='action' />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    fullWidth
                                    size='small'
                                    variant='outlined'
                                    label='Proveedor'
                                    name='provider'
                                    value={filters.provider}
                                    onChange={handleFilterChange}
                                    className={classes.filterField}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <SearchIcon color='action' />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size='small'
                                    variant='outlined'
                                    label='Tipo de servicio'
                                    name='serviceType'
                                    value={filters.serviceType}
                                    onChange={handleFilterChange}
                                    className={classes.filterField}
                                >
                                    {serviceTypeOptions.map(option => (
                                        <MenuItem key={option.value || 'all-services'} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size='small'
                                    variant='outlined'
                                    label='Estado'
                                    name='status'
                                    value={filters.status}
                                    onChange={handleFilterChange}
                                    className={classes.filterField}
                                >
                                    {billStatusOptions.map(option => (
                                        <MenuItem key={option.value || 'all-status'} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    fullWidth
                                    size='small'
                                    variant='outlined'
                                    label='Desde'
                                    type='date'
                                    name='dateFrom'
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                    className={classes.filterField}
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <EventOutlinedIcon color='action' />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    fullWidth
                                    size='small'
                                    variant='outlined'
                                    label='Hasta'
                                    type='date'
                                    name='dateTo'
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                    className={classes.filterField}
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <EventOutlinedIcon color='action' />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} className={classes.filterActions}>
                                <Button
                                    className={classes.secondaryTopActionButton}
                                    onClick={handleClearFilters}
                                    disabled={!Object.values(filters).some(value => value)}
                                >
                                    Limpiar filtros
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    <Box className={classes.listContainer}>
                        {loadingBills ? (
                            <Box className={classes.centeredState}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : (
                            <TableContainer>
                                <Table>
                                    <TableHead className={classes.tableHead}>
                                        <TableRow>
                                            <TableCell className={classes.headCell}>Factura</TableCell>
                                            <TableCell className={classes.headCell}>Proveedor</TableCell>
                                            <TableCell className={classes.headCell}>Periodo</TableCell>
                                            <TableCell className={classes.headCell}>Ubicacion</TableCell>
                                            <TableCell className={classes.headCell}>Emision</TableCell>
                                            <TableCell className={classes.headCell}>Vencimiento</TableCell>
                                            <TableCell className={classes.headCell}>Consumo</TableCell>
                                            <TableCell className={classes.headCell}>Estado</TableCell>
                                            <TableCell className={classes.headCell}>Precisión</TableCell>
                                            <TableCell className={classes.headCell} align='right'>Total</TableCell>
                                            <TableCell className={classes.headCell} align='right'>Acciones</TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {paginatedBills.map(bill => {
                                            const statusConfig = getStatusConfig(theme, bill.status);

                                            return (
                                                <TableRow key={bill.id} className={classes.bodyRow}>
                                                    <TableCell className={classes.bodyCell}>
                                                        <Box display='flex' alignItems='center' style={{ gap: 8 }}>
                                                            {/* <Box
                                                                className={classes.serviceTypeTag}
                                                                style={{
                                                                    backgroundColor: getServiceTypeBg(bill.tipo),
                                                                    color: getServiceTypeColor(bill.tipo),
                                                                }}
                                                            >
                                                                {(bill.tipo || 'E').charAt(0).toUpperCase()}
                                                            </Box> */}
                                                            <Typography
                                                                className={classes.code}
                                                                onClick={() => handleOpenBillDetail(bill)}
                                                            >
                                                                {bill.code}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell className={classes.bodyCell}>
                                                        <Typography className={classes.client}>{bill.client}</Typography>
                                                    </TableCell>
                                                    <TableCell className={classes.bodyCell}>
                                                        <Typography className={classes.period}>{bill.period}</Typography>
                                                    </TableCell>
                                                    <TableCell className={classes.bodyCell}>
                                                        <Box className={classes.locationCell}>
                                                            <LocationOnOutlinedIcon fontSize='small' style={{ color: theme.palette.primary.main }} />
                                                            <span>{bill.location}</span>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell className={classes.bodyCell}>{bill.issuedAt}</TableCell>
                                                    <TableCell className={classes.bodyCell}>{bill.dueAt}</TableCell>
                                                    <TableCell className={classes.bodyCell}>{bill.consumption}</TableCell>
                                                    <TableCell className={classes.bodyCell}>
                                                        <Chip
                                                            label={statusConfig.label}
                                                            className={classes.statusChip}
                                                            style={{
                                                                backgroundColor: statusConfig.backgroundColor,
                                                                color: statusConfig.color,
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell className={classes.bodyCell}>
                                                        {bill.score !== null && bill.score !== undefined ? (
                                                            <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                                <Box
                                                                    className={classes.scoreChip}
                                                                    style={
                                                                        bill.score >= 86
                                                                            ? { backgroundColor: '#dcfce7', color: '#166534' }
                                                                            : bill.score >= 60
                                                                                ? { backgroundColor: '#fef9c3', color: '#854d0e' }
                                                                                : { backgroundColor: '#fee2e2', color: '#991b1b' }
                                                                    }
                                                                >
                                                                    {bill.score}%
                                                                </Box>
                                                                {/* {bill.campos_corregidos_count > 0 && (
                                                                    <Tooltip title={`${bill.campos_corregidos_count} campo${bill.campos_corregidos_count !== 1 ? 's' : ''} editado${bill.campos_corregidos_count !== 1 ? 's' : ''}`}>
                                                                        <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11, color: '#2586bc', fontWeight: 700, cursor: 'default' }}>
                                                                            <EditOutlinedIcon style={{ fontSize: 13 }} />
                                                                            {bill.campos_corregidos_count}
                                                                        </Box>
                                                                    </Tooltip>
                                                                )} */}
                                                            </Box>
                                                        ) : '-'}
                                                    </TableCell>
                                                    <TableCell className={classes.bodyCell} align='right'>
                                                        <Typography className={classes.totalValue}>
                                                            {currencyFormatter.format(Number(bill.total || 0))}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell className={classes.bodyCell}>
                                                        <Box className={`${classes.actionsCell} ${classes.actionsRow}`}>                                                            
                                                            <Tooltip title='Mas acciones'>
                                                                <IconButton
                                                                    className={classes.iconActionButton}
                                                                    onClick={event => handleOpenRowActionsMenu(event, bill)}
                                                                    aria-label='Abrir acciones'
                                                                >
                                                                    <MoreVertIcon fontSize='small' />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}

                                        {filteredBills.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={11} style={{ border: 'none' }}>
                                                    <Box className={classes.emptyState}>
                                                        <ReceiptOutlinedIcon className={classes.emptyStateIcon} />
                                                        <Typography className={classes.emptyStateText}>
                                                            No hay facturas que coincidan con los filtros seleccionados.
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                    {!loadingBills && (
                        <TablePagination
                            component='div'
                            count={totalBillsCount}
                            page={page}
                            onChangePage={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onChangeRowsPerPage={handleChangeRowsPerPage}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            labelRowsPerPage='Filas por pagina'
                            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                        />
                    )}

                    <Menu
                        anchorEl={rowActionsAnchorEl}
                        keepMounted
                        open={Boolean(rowActionsAnchorEl)}
                        onClose={handleCloseRowActionsMenu}
                    >
                        <MenuItem onClick={handleEditRowBill}>Editar</MenuItem>
                        <MenuItem onClick={handleViewRowBill} disabled={!selectedRowBill?.archivoUrl}>
                            Descargar factura
                        </MenuItem>
                        <MenuItem onClick={handleDeleteRowBillRequest} style={{ color: '#d32f2f' }}>
                            Eliminar
                        </MenuItem>
                    </Menu>

                    <Menu
                        anchorEl={exportAnchorEl}
                        keepMounted
                        open={exportMenuOpen}
                        onClose={handleCloseExportMenu}
                    >
                        <MenuItem onClick={handleExportCsv}>Exportar CSV</MenuItem>
                    </Menu>

                    <Dialog open={deleteConfirmOpen} onClose={handleCancelDelete} maxWidth='xs' fullWidth>
                        <DialogTitle>Eliminar factura</DialogTitle>
                        <DialogContent>
                            <Typography style={{ marginBottom: 8 }}>
                                ¿Confirmás la eliminación de{' '}
                                <strong>{billToDelete?.code || `Factura #${billToDelete?.id}`}</strong>?
                                Esta acción no se puede deshacer.
                            </Typography>
                            {deleteError && (
                                <Typography style={{ color: '#d32f2f', fontSize: 13, marginTop: 8 }}>
                                    {deleteError}
                                </Typography>
                            )}
                        </DialogContent>
                        <Box style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '8px 24px 20px' }}>
                            <Button onClick={handleCancelDelete} disabled={deletingBill}>
                                Cancelar
                            </Button>
                            <Button
                                variant='contained'
                                disableElevation
                                disabled={deletingBill}
                                onClick={handleConfirmDelete}
                                style={{ backgroundColor: '#d32f2f', color: '#fff' }}
                            >
                                {deletingBill ? <CircularProgress size={18} style={{ color: '#fff' }} /> : 'Eliminar'}
                            </Button>
                        </Box>
                    </Dialog>

                    <Dialog open={rejectAllConfirmOpen} onClose={() => setRejectAllConfirmOpen(false)} maxWidth='xs' fullWidth>
                        <DialogTitle>Rechazar todas las facturas</DialogTitle>
                        <DialogContent>
                            <Typography>
                                ¿Confirmás el rechazo de las <strong>{processedReviewJobs.length}</strong> factura{processedReviewJobs.length !== 1 ? 's' : ''} pendientes? Esta acción no se puede deshacer.
                            </Typography>
                        </DialogContent>
                        <Box style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '8px 24px 20px' }}>
                            <Button onClick={() => setRejectAllConfirmOpen(false)}>Cancelar</Button>
                            <Button
                                variant='contained'
                                disableElevation
                                onClick={() => { processedReviewJobs.forEach(job => handleSetProcessedJobDecision(job.id, 'rejected')); setRejectAllConfirmOpen(false); }}
                                style={{ backgroundColor: '#d32f2f', color: '#fff' }}
                            >
                                Rechazar todo
                            </Button>
                        </Box>
                    </Dialog>

                    <Dialog
                        open={uploadModalOpen}
                        onClose={handleCloseUploadModal}
                        fullWidth
                        maxWidth='xl'
                        className={`${classes.uploadDialog} ${uploadStep !== 'options' ? classes.uploadDialogCompact : ''}`}
                    >
                        <DialogTitle disableTypography className={classes.uploadDialogTitle}>
                            <Box className={classes.uploadDialogHeader}>
                                <Box>
                                    <Box className={classes.uploadDialogEyebrow}>
                                        <CloudUploadOutlinedIcon fontSize='small' />
                                        Nueva carga
                                    </Box>
                                    <Typography variant='h5' className={classes.uploadDialogTitleText}>
                                        {uploadStep === 'options'
                                            ? 'Elegi como queres cargar las facturas'
                                            : 'Subi las facturas desde tu computadora'}
                                    </Typography>
                                    <Typography className={classes.uploadDialogSubtitle}>
                                        {uploadStep === 'options'
                                            ? 'Esta maqueta prepara los dos caminos del flujo. Por ahora solo definimos la experiencia inicial para que luego conectemos cada proceso.'
                                            : 'Arrastra uno o varios archivos a la zona de carga o seleccionalos manualmente desde tu PC.'}
                                    </Typography>
                                </Box>

                                <IconButton
                                    onClick={handleCloseUploadModal}
                                    className={classes.uploadDialogCloseButton}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </DialogTitle>

                        <DialogContent className={`${classes.uploadDialogContent} ${uploadStep === 'options' ? classes.uploadDialogContentOptions : ''}`}>
                            {uploadStep === 'options' ? (
                                <Grid container spacing={2} className={classes.uploadOptionsGrid}>
                                    <Grid item xs={12} md={6}>
                                        {isUploadBlocked ? (
                                            <Box
                                                className={`${classes.uploadOptionCard} ${classes.uploadOptionCardPremium}`}
                                                onClick={() => history.push('/pagos')}
                                            >
                                                <Box>
                                                    <Box className={`${classes.uploadOptionIcon} ${classes.premiumOptionIcon}`}>
                                                        <CloudUploadOutlinedIcon fontSize='large' />
                                                    </Box>
                                                    <Typography variant='h6' className={classes.uploadOptionTitle}>
                                                        Subir y analizar con IA
                                                    </Typography>
                                                    <Typography className={classes.uploadOptionDescription}>
                                                        Subí tus facturas en lote y dejá que la inteligencia artificial extraiga y complete los datos automáticamente.
                                                    </Typography>
                                                    <Box className={classes.premiumFeatureList}>
                                                        {['Carga masiva de archivos', 'Extracción automática de datos', 'Revisión y corrección asistida'].map(feature => (
                                                            <Box key={feature} className={classes.premiumFeatureItem}>
                                                                <DoneOutlinedIcon />
                                                                {feature}
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Box>
                                                <Button
                                                    variant='contained'
                                                    fullWidth
                                                    disableElevation
                                                    className={classes.upgradeButton}
                                                >
                                                    Ver planes disponibles
                                                </Button>
                                            </Box>
                                        ) : (
                                            <Box
                                                className={`${classes.uploadOptionCard} ${uploadOption === 'files' ? classes.uploadOptionCardSelected : ''}`}
                                                onClick={() => handleContinueUpload('files')}
                                            >
                                                <Box className={classes.uploadOptionIcon}>
                                                    <CloudUploadOutlinedIcon fontSize='large' />
                                                </Box>
                                                <Typography variant='h6' className={classes.uploadOptionTitle}>
                                                    Subir archivos
                                                </Typography>
                                                <Typography className={classes.uploadOptionDescription}>
                                                    Pensado para arrastrar facturas o seleccionar uno o varios archivos desde tu equipo y procesarlos en lote.
                                                </Typography>
                                                <Box className={classes.uploadOptionTag}>Ideal para carga masiva</Box>
                                            </Box>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Box
                                            className={`${classes.uploadOptionCard} ${uploadOption === 'manual' ? classes.uploadOptionCardSelected : ''}`}
                                            onClick={() => handleContinueUpload('manual')}
                                        >
                                            <Box className={classes.uploadOptionIcon}>
                                                <NoteAddOutlinedIcon fontSize='large' />
                                            </Box>
                                            <Typography variant='h6' className={classes.uploadOptionTitle}>
                                                Completar manualmente
                                            </Typography>
                                            <Typography className={classes.uploadOptionDescription}>
                                                Para crear una factura paso a paso cargando los datos clave desde un
                                                formulario.
                                            </Typography>
                                            <Box className={classes.uploadOptionTag}>Ideal para casos puntuales</Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            ) : (
                                <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                                    <input
                                        id='bill-upload-input'
                                        type='file'
                                        multiple
                                        className={classes.hiddenInput}
                                        onChange={handleUploadInputChange}
                                    />

                                    {/* Zona de carga: visible solo cuando no hay archivos ni jobs activos */}
                                    {selectedFiles.length === 0 && processedReviewJobs.length === 0 && processingOnlyJobs.length === 0 && (
                                        <Box style={{ display: 'flex', flexDirection: 'column' }}>
                                            <Box
                                                className={`${classes.uploadDropzone} ${isDragActive ? classes.uploadDropzoneActive : ''}`}
                                                onDragOver={handleDragOver}
                                                onDragLeave={handleDragLeave}
                                                onDrop={handleDropFiles}
                                            >
                                                <Box className={classes.uploadDropzoneIcon}>
                                                    <CloudUploadOutlinedIcon fontSize='large' />
                                                </Box>
                                                <Typography variant='h6' className={classes.uploadDropzoneTitle}>
                                                    Arrastra tus archivos aca
                                                </Typography>
                                                <Typography className={classes.uploadDropzoneText}>
                                                    Tambien podes seleccionar multiples facturas manualmente desde tu computadora.
                                                </Typography>
                                                <Button
                                                    variant='contained'
                                                    disableElevation
                                                    component='label'
                                                    htmlFor='bill-upload-input'
                                                    className={classes.primaryActionButton}
                                                >
                                                    Seleccionar archivos
                                                </Button>
                                                <Typography className={classes.uploadDropzoneHint}>
                                                    Formatos de ejemplo: PDF, JPG, PNG, XLSX.
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}

                                    {/* Lista unificada: seleccionados → procesados → en proceso */}
                                    {(selectedFiles.length > 0 || processedReviewJobs.length > 0 || processingOnlyJobs.length > 0) && (
                                        <Box className={classes.uploadFilesRight} style={{ gridColumn: '1 / -1' }}>
                                            {/* Barra de selección masiva (solo cuando hay procesadas) */}
                                            {processedReviewJobs.length > 0 && (
                                                <Box className={classes.uploadSelectionBar}>
                                                    <Box className={classes.uploadSelectionInfo}>
                                                        <Checkbox
                                                            color='primary'
                                                            checked={allProcessedJobsSelected}
                                                            indeterminate={someProcessedJobsSelected}
                                                            onChange={handleToggleAllProcessedJobSelections}
                                                            inputProps={{ 'aria-label': 'Seleccionar todas las facturas procesadas' }}
                                                        />
                                                        <Typography className={classes.uploadSelectionLabel}>
                                                            {selectedProcessedJobIds.length > 0
                                                                ? `${selectedProcessedJobIds.length} seleccionada${selectedProcessedJobIds.length === 1 ? '' : 's'}`
                                                                : 'Seleccionar todas'}
                                                        </Typography>
                                                    </Box>
                                                    <Box className={classes.uploadIconActions}>
                                                        <IconButton
                                                            size='small'
                                                            className={classes.uploadIconButton}
                                                            style={{
                                                                color: selectedProcessedJobIds.length > 0
                                                                    ? theme.palette.status.success
                                                                    : theme.palette.action.disabled,
                                                            }}
                                                            onClick={() => handleApplyBulkProcessedJobDecision('accepted')}
                                                            disabled={selectedProcessedJobIds.length === 0}
                                                            aria-label='Aceptar facturas seleccionadas'
                                                        >
                                                            <DoneOutlinedIcon fontSize='small' />
                                                        </IconButton>
                                                        <IconButton
                                                            size='small'
                                                            className={classes.uploadIconButton}
                                                            style={{
                                                                color: selectedProcessedJobIds.length > 0
                                                                    ? theme.palette.error.main
                                                                    : theme.palette.action.disabled,
                                                            }}
                                                            onClick={() => handleApplyBulkProcessedJobDecision('rejected')}
                                                            disabled={selectedProcessedJobIds.length === 0}
                                                            aria-label='Rechazar facturas seleccionadas'
                                                        >
                                                            <CloseIcon fontSize='small' />
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                            )}

                                            <Box className={classes.uploadJobsList} style={{ gap: 6 }}>
                                                {/* 1. Archivos seleccionados (pendientes de procesar) */}
                                                {selectedFiles.map((file, index) => (
                                                    <Box key={`${file.name}-${index}`} className={classes.fileItem}>
                                                        <Box className={classes.fileItemInfo}>
                                                            <Box className={classes.fileItemIcon}>
                                                                <DescriptionOutlinedIcon fontSize='small' />
                                                            </Box>
                                                            <Box>
                                                                <Typography className={classes.fileItemName}>
                                                                    {file.name}
                                                                </Typography>
                                                                <Typography className={classes.fileItemMeta}>
                                                                    {formatFileSize(file.size)}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        <Box className={classes.fileItemActions}>
                                                            <Box className={`${classes.uploadOptionTag} ${classes.compactTag}`}>
                                                                Listo
                                                            </Box>
                                                            <IconButton
                                                                size='small'
                                                                onClick={() => handleRemoveSelectedFile(index)}
                                                                className={classes.uploadDialogCloseButton}
                                                            >
                                                                <CloseIcon fontSize='small' />
                                                            </IconButton>
                                                        </Box>
                                                    </Box>
                                                ))}

                                                {/* 2. Facturas ya procesadas (pendientes de decision) */}
                                                {processedReviewJobs.map(job => renderUploadJobCard(job, {
                                                    compact: true,
                                                    hideScore: false,
                                                    statusLabel: 'Pendiente de revision',
                                                    statusStyle: {
                                                        backgroundColor: theme.palette.surface.accentSoft,
                                                        color: theme.palette.primary.main,
                                                    },
                                                    leadingControl: (
                                                        <Checkbox
                                                            color='primary'
                                                            checked={selectedProcessedJobIds.includes(job.id)}
                                                            onChange={() => handleToggleProcessedJobSelection(job.id)}
                                                            inputProps={{ 'aria-label': `Seleccionar ${job.filename}` }}
                                                        />
                                                    ),
                                                    extraActions: (
                                                        <Box className={classes.uploadIconActions}>
                                                            <IconButton
                                                                size='small'
                                                                className={classes.uploadIconButton}
                                                                style={{ color: theme.palette.primary.main }}
                                                                onClick={() => handleOpenProcessedJobDetail(job)}
                                                                aria-label='Ver factura'
                                                            >
                                                                <VisibilityOutlinedIcon fontSize='small' />
                                                            </IconButton>
                                                            <IconButton
                                                                size='small'
                                                                className={classes.uploadIconButton}
                                                                style={{ color: theme.palette.status.success }}
                                                                onClick={() => handleSetProcessedJobDecision(job.id, 'accepted')}
                                                                aria-label='Aceptar factura'
                                                            >
                                                                <DoneOutlinedIcon fontSize='small' />
                                                            </IconButton>
                                                            <IconButton
                                                                size='small'
                                                                className={classes.uploadIconButton}
                                                                style={{ color: theme.palette.error.main }}
                                                                onClick={() => handleSetProcessedJobDecision(job.id, 'rejected')}
                                                                aria-label='Rechazar factura'
                                                            >
                                                                <CloseIcon fontSize='small' />
                                                            </IconButton>
                                                        </Box>
                                                    ),
                                                }))}

                                                {/* 3. Archivos en procesamiento */}
                                                {processingOnlyJobs.map(job => (
                                                    <Box key={job.id} className={classes.processingJobRow}>
                                                        <CircularProgress size={13} thickness={4} />
                                                        <Typography className={classes.processingJobRowName} title={job.filename}>
                                                            {job.filename}
                                                        </Typography>
                                                        <Box className={classes.processingJobRowBadge}>
                                                            Procesando
                                                        </Box>
                                                        <Button
                                                            size='small'
                                                            startIcon={<StopOutlinedIcon style={{ fontSize: 14 }} />}
                                                            onClick={() => handleAbortJob(job.id)}
                                                            style={{ textTransform: 'none', fontWeight: 600, fontSize: 12, padding: '2px 8px', flexShrink: 0 }}
                                                        >
                                                            Abortar
                                                        </Button>
                                                    </Box>
                                                ))}
                                            </Box>

                                            {uploadFeedback.message && (
                                                <Box
                                                    className={`${classes.uploadInlineMessage} ${uploadFeedback.type === 'error' ? classes.uploadInlineMessageError : uploadFeedback.type === 'warning' ? classes.uploadInlineMessageWarning : ''}`}
                                                >
                                                    {uploadFeedback.message}
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            )}


                            {uploadProgress.phase !== 'idle' && (() => {
                                const { phase, completedUploads, totalUploads, completedJobs, totalJobs } = uploadProgress;
                                const uploadPct = totalUploads > 0 ? (completedUploads / totalUploads) * 50 : 0;
                                const jobPct = totalJobs > 0 ? (completedJobs / totalJobs) * 50 : 0;
                                const pct = phase === 'done'
                                    ? 100
                                    : phase === 'uploading'
                                        ? Math.round(uploadPct)
                                        : Math.round(50 + jobPct);

                                return (
                                    <Box className={classes.uploadProgressBar}>
                                        <Box className={classes.uploadProgressHeader}>
                                            <Typography className={classes.uploadProgressLabel}>
                                                {phase === 'uploading' && 'Subiendo archivos'}
                                                {phase === 'processing' && 'Procesando'}
                                                {phase === 'done' && 'Finalizado'}
                                            </Typography>
                                            <Typography className={classes.uploadProgressPct}>
                                                {pct}%
                                            </Typography>
                                        </Box>
                                        <Box className={classes.uploadProgressTrack}>
                                            <Box
                                                className={classes.uploadProgressFill}
                                                style={{
                                                    width: `${pct}%`,
                                                    background: phase === 'done'
                                                        ? '#22c55e'
                                                        : 'linear-gradient(90deg, #2586bc, #60a5fa)',
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                );
                            })()}

                            <Box className={`${classes.uploadDialogActions} ${uploadStep === 'options' ? classes.uploadDialogActionsOptions : ''}`}>
                                {uploadStep === 'options' ? (
                                    <Button
                                        onClick={handleCloseUploadModal}
                                        className={classes.secondaryActionButton}
                                    >
                                        Cancelar
                                    </Button>
                                ) : (submittingFiles || uploadProgress.phase !== 'idle' || processedReviewJobs.length > 0) ? (
                                    <>
                                        <Button
                                            className={classes.secondaryActionButton}
                                            disabled={processedReviewJobs.length === 0}
                                            onClick={() => setRejectAllConfirmOpen(true)}
                                        >
                                            Rechazar todo
                                        </Button>
                                        <Button
                                            variant='contained'
                                            disableElevation
                                            className={classes.primaryActionButton}
                                            disabled={processedReviewJobs.length === 0}
                                            onClick={() => processedReviewJobs.forEach(job => handleSetProcessedJobDecision(job.id, 'accepted'))}
                                        >
                                            Aceptar todo
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button
                                            onClick={handleBackUploadStep}
                                            className={classes.secondaryActionButton}
                                        >
                                            Volver
                                        </Button>
                                        <Button
                                            variant='contained'
                                            disableElevation
                                            className={classes.primaryActionButton}
                                            onClick={handleSubmitFiles}
                                        >
                                            Procesar archivos
                                        </Button>
                                    </>
                                )}
                            </Box>
                        </DialogContent>
                    </Dialog>

                    <Dialog
                        open={detailModalOpen}
                        onClose={handleCloseBillDetail}
                        fullWidth
                        maxWidth='lg'
                        className={classes.detailDialog}
                    >
                        <DialogTitle disableTypography className={classes.uploadDialogTitle}>
                            <Box className={classes.uploadDialogHeader}>
                                <Box>
                                    <Typography variant='h5' className={classes.uploadDialogTitleText}>
                                        {detailMode === 'create'
                                            ? 'Crear factura manual'
                                            : 'Ver o editar factura'}
                                    </Typography>
                                    <Typography className={classes.uploadDialogSubtitle}>
                                        {detailMode === 'create'
                                            ? 'Completa los datos manualmente para generar una nueva factura.'
                                            : 'Revisa y ajusta los datos extraidos o registrados para la factura seleccionada.'}
                                    </Typography>
                                </Box>
                                <IconButton
                                    onClick={handleCloseBillDetail}
                                    className={classes.uploadDialogCloseButton}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </DialogTitle>

                        <DialogContent className={classes.detailDialogContent}>
                            {detailLoading ? (
                                <Box className={classes.centeredState}>
                                    <CircularProgress size={28} />
                                </Box>
                            ) : selectedBillDetail ? (
                                <BillInvoiceEditor
                                    bill={selectedBillDetail}
                                    editable
                                    onSave={handleSaveBillEditor}
                                    onCancel={handleCloseBillDetail}
                                    onDelete={handleDeleteBillEditor}
                                    onRefresh={handleRefreshBillDetail}
                                    onSelectFile={() => {
                                        handleCloseBillDetail();
                                        handleOpenUploadModal();
                                    }}
                                    onViewFile={handleDownloadBillDocument}
                                />
                            ) : null}
                        </DialogContent>
                    </Dialog>

                    {/* Hierarchy selector dialog */}
                    <Dialog
                        open={jerarquiaSelectorOpen}
                        onClose={handleCloseJerarquiaSelector}
                        maxWidth='xs'
                        fullWidth
                    >
                        <DialogTitle disableTypography>
                            <Box display='flex' alignItems='center' justifyContent='space-between'>
                                <Typography variant='h6' style={{ fontWeight: 700, fontSize: 15, color: theme.palette.text.primary }}>
                                    Seleccionar localización
                                </Typography>
                                <IconButton onClick={handleCloseJerarquiaSelector} size='small'>
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </DialogTitle>
                        <DialogContent style={{ paddingTop: 8, paddingBottom: 16 }}>
                            <TextField
                                fullWidth
                                size='small'
                                variant='outlined'
                                placeholder='Buscar localización...'
                                value={jerarquiaSearch}
                                onChange={e => setJerarquiaSearch(e.target.value)}
                                className={classes.filterField}
                                style={{ marginBottom: 12 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <SearchIcon color='action' />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {jerarquiaNodesLoading ? (
                                <Box className={classes.centeredState} style={{ minHeight: 100 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : (
                                <Box style={{ maxHeight: 320, overflowY: 'auto' }}>
                                    {jerarquiaNodes
                                        .filter(node =>
                                            !jerarquiaSearch ||
                                            String(node.nombre || '').toLowerCase().includes(jerarquiaSearch.toLowerCase())
                                        )
                                        .map(node => (
                                            <Box
                                                key={node.id}
                                                className={classes.jerarquiaListItem}
                                                onClick={() => handleSelectJerarquia(node)}
                                            >
                                                <Typography style={{ fontSize: 13, fontWeight: 600, color: theme.palette.text.primary }}>
                                                    {node.nombre}
                                                </Typography>
                                            </Box>
                                        ))}
                                    {!jerarquiaNodesLoading && jerarquiaNodes.filter(node =>
                                        !jerarquiaSearch ||
                                        String(node.nombre || '').toLowerCase().includes(jerarquiaSearch.toLowerCase())
                                    ).length === 0 && (
                                        <Typography style={{ fontSize: 13, color: theme.palette.text.disabled, padding: theme.spacing(1, 0) }}>
                                            No se encontraron instalaciones.
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </DialogContent>
                    </Dialog>

                    {/* Orphan bills review dialog */}
                    <Dialog
                        open={orphanDialogOpen}
                        onClose={handleCloseOrphanDialog}
                        maxWidth='md'
                        fullWidth
                    >
                        <DialogTitle disableTypography>
                            <Box display='flex' alignItems='center' justifyContent='space-between'>
                                <Box display='flex' alignItems='center' style={{ gap: 10 }}>
                                    <RemoveCircleOutlineOutlinedIcon style={{ color: theme.palette.warning.main }} />
                                    <Box>
                                        <Typography variant='h6' style={{ fontWeight: 700, fontSize: 15, color: theme.palette.text.primary }}>
                                            Facturas sin vincular
                                        </Typography>
                                        <Typography style={{ fontSize: 12, color: theme.palette.text.secondary, marginTop: 1 }}>
                                            {orphanSummary.count} factura{orphanSummary.count !== 1 ? 's' : ''} pendiente{orphanSummary.count !== 1 ? 's' : ''} de asignación
                                        </Typography>
                                    </Box>
                                </Box>
                                <IconButton onClick={handleCloseOrphanDialog} size='small'>
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </DialogTitle>
                        <DialogContent dividers style={{ maxHeight: 620, overflowY: 'auto' }}>
                            {orphanItems.length === 0 ? (
                                <Box className={classes.centeredState}>
                                    <Typography style={{ fontSize: 13, color: theme.palette.text.disabled }}>
                                        No hay facturas huérfanas para revisar.
                                    </Typography>
                                </Box>
                            ) : (
                                orphanItems.map(bill => {
                                    // bill.jerarquia_sugerida_id = 1468;
                                    // bill.jerarquia_sugerida_nombre = "Sede Central";
                                    const isLoading = linkingBill?.id === bill.id && linkingBill?.tipo === bill.tipo;
                                    const hasSuggestion = Boolean(bill.jerarquia_sugerida_id);
                                    const confidence = bill.score_sugerencia != null
                                        ? `${Math.round(bill.score_sugerencia * 100)}% de confianza`
                                        : null;

                                    return (
                                        <Box key={`${bill.tipo}-${bill.id}`} className={classes.orphanCard}>
                                            <Box className={classes.orphanCardHeader}>
                                                <Typography className={classes.orphanCardTitle}>
                                                    {bill.numero_factura || `Factura #${bill.id}`}
                                                </Typography>
                                                <Chip
                                                    label={bill.tipo}
                                                    size='small'
                                                    style={{
                                                        fontSize: 11,
                                                        height: 20,
                                                        backgroundColor: getServiceTypeBg(bill.tipo),
                                                        color: getServiceTypeColor(bill.tipo),
                                                    }}
                                                />
                                            </Box>
                                            <Typography className={classes.orphanCardMeta}>
                                                {[bill.proveedor_nombre, bill.periodo_desde && bill.periodo_hasta
                                                    ? `${normalizeDateToDisplay(bill.periodo_desde)} - ${normalizeDateToDisplay(bill.periodo_hasta)}`
                                                    : null
                                                ].filter(Boolean).join(' · ')}
                                            </Typography>
                                            {bill.cliente_domicilio && (
                                                <Typography className={classes.orphanCardMeta} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <LocationOnOutlinedIcon style={{ fontSize: 14 }} />
                                                    {bill.cliente_domicilio}
                                                </Typography>
                                            )}
                                            {hasSuggestion && (
                                                <Box className={classes.suggestionBadge}>
                                                    <AutoAwesomeIcon style={{ fontSize: 16, color: 'inherit' }} />
                                                    <Typography className={classes.suggestionBadgeText}>
                                                        Sugerencia: {bill.jerarquia_sugerida_nombre}
                                                    </Typography>
                                                    {confidence && (
                                                        <Typography className={classes.suggestionConfidence}>
                                                            {confidence}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            )}
                                            <Box className={classes.orphanActions}>
                                                {hasSuggestion && (
                                                    <Button
                                                        variant='contained'
                                                        disableElevation
                                                        size='small'
                                                        color='primary'
                                                        className={classes.orphanAcceptBtn}
                                                        disabled={isLoading}
                                                        onClick={() => handleAcceptSuggestion(bill)}
                                                        startIcon={isLoading ? <CircularProgress size={12} /> : <DoneOutlinedIcon />}
                                                    >
                                                        Aceptar sugerencia
                                                    </Button>
                                                )}
                                                <Button
                                                    variant='outlined'
                                                    size='small'
                                                    className={classes.orphanSecondaryBtn}
                                                    disabled={isLoading}
                                                    onClick={() => handleOpenJerarquiaSelector(bill)}
                                                    startIcon={<LocationOnOutlinedIcon />}
                                                >
                                                    {hasSuggestion ? 'Elegir otra' : 'Vincular'}
                                                </Button>
                                                {hasSuggestion && (
                                                    <Button
                                                        size='small'
                                                        className={classes.orphanDiscardBtn}
                                                        disabled={isLoading}
                                                        onClick={() => handleDiscardSuggestion(bill)}
                                                    >
                                                        No, gracias
                                                    </Button>
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })
                            )}
                        </DialogContent>
                    </Dialog>

                    {/* Services management dialog */}
                    <Dialog
                        open={servicesDialogOpen}
                        onClose={handleCloseServicesDialog}
                        maxWidth='md'
                        fullWidth
                        className={classes.servicesDialog}
                    >
                        <DialogTitle disableTypography>
                            <Box display='flex' alignItems='center' justifyContent='space-between'>
                                <Box display='flex' alignItems='center' style={{ gap: 10 }}>
                                    <TuneIcon style={{ color: theme.palette.primary.main }} />
                                    <Box>
                                        <Typography variant='h6' style={{ fontWeight: 700, fontSize: 15, color: theme.palette.text.primary }}>
                                            {servicesStep === 'select' ? 'Gestionar Servicios' : `Servicios de ${servicesNode?.nombre}`}
                                        </Typography>
                                        <Typography style={{ fontSize: 12, color: theme.palette.text.secondary, marginTop: 1 }}>
                                            {servicesStep === 'select'
                                                ? 'Seleccioná una localización para configurar sus servicios'
                                                : 'Configurá los servicios de electricidad, agua y gas'}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box display='flex' alignItems='center' style={{ gap: 8 }}>
                                    {servicesStep === 'services' && (
                                        <Button size='small' onClick={() => setServicesStep('select')} style={{ textTransform: 'none' }}>
                                            Cambiar localización
                                        </Button>
                                    )}
                                    <IconButton onClick={handleCloseServicesDialog} size='small'>
                                        <CloseIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                        </DialogTitle>
                        <DialogContent style={{ paddingTop: 8, paddingBottom: 24 }}>
                            {servicesStep === 'select' ? (
                                <Box className={classes.servicesLocationSelector}>
                                    <TextField
                                        fullWidth
                                        size='small'
                                        variant='outlined'
                                        placeholder='Buscar localización...'
                                        value={servicesNodeSearch}
                                        onChange={e => setServicesNodeSearch(e.target.value)}
                                        className={classes.filterField}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position='start'>
                                                    <SearchIcon color='action' />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    {servicesNodesLoading ? (
                                        <Box className={classes.centeredState} style={{ minHeight: 100 }}>
                                            <CircularProgress size={24} />
                                        </Box>
                                    ) : (
                                        <Box style={{ maxHeight: 340, overflowY: 'auto' }}>
                                            {servicesNodes
                                                .filter(node =>
                                                    !servicesNodeSearch ||
                                                    String(node.nombre || '').toLowerCase().includes(servicesNodeSearch.toLowerCase())
                                                )
                                                .map(node => (
                                                    <Box
                                                        key={node.id}
                                                        className={classes.jerarquiaListItem}
                                                        onClick={() => handleSelectServicesNode(node)}
                                                    >
                                                        <Typography style={{ fontSize: 13, fontWeight: 600, color: theme.palette.text.primary }}>
                                                            {node.nombre}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            {!servicesNodesLoading && servicesNodes.filter(node =>
                                                !servicesNodeSearch ||
                                                String(node.nombre || '').toLowerCase().includes(servicesNodeSearch.toLowerCase())
                                            ).length === 0 && (
                                                <Typography style={{ fontSize: 13, color: theme.palette.text.disabled, padding: theme.spacing(1, 0) }}>
                                                    No se encontraron localizaciones.
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            ) : servicesLoading ? (
                                <Box className={classes.centeredState} style={{ minHeight: 140 }}>
                                    <CircularProgress size={28} />
                                </Box>
                            ) : (
                                <Box className={classes.servicesGrid}>
                                    {renderServiceCard('electricidad', 'Electricidad', '#F59E0B', <FlashOn style={{ fontSize: 16 }} />, electricityService, setElectricityService)}
                                    {renderServiceCard('agua', 'Agua', '#3B82F6', <Opacity style={{ fontSize: 16 }} />, waterService, setWaterService)}
                                    {renderServiceCard('gas', 'Gas', '#EF4444', <Whatshot style={{ fontSize: 16 }} />, gasService, setGasService)}
                                </Box>
                            )}
                        </DialogContent>
                    </Dialog>

                    <Snackbar
                        open={servicesNotification.open}
                        autoHideDuration={2500}
                        onClose={() => setServicesNotification(prev => ({ ...prev, open: false }))}
                    >
                        <Alert
                            severity={servicesNotification.severity}
                            onClose={() => setServicesNotification(prev => ({ ...prev, open: false }))}
                        >
                            {servicesNotification.message}
                        </Alert>
                    </Snackbar>
                </Container>
            </Box>
        </>
    );
}
